import moment from 'moment-timezone';
import {
	getContractFrequencyPresentation,
	getContractFrequencyReviewMessage,
} from 'components/DataFieldManager/ContractFrequencyId/contractFrequencyReview.model';
import { isContractFrequencyCreationIssueResolved } from './contractFrequencyCreation.model';

export const REVIEW_STATUS = {
	READY: 'ready',
	AUTO_CORRECTED: 'auto_corrected',
	REVIEW_REQUIRED: 'review_required',
	BLOCKED: 'blocked',
};

export const REVIEW_FILTER = {
	ALL: 'all',
	...REVIEW_STATUS,
};

export const LOCATION_PRESENTATION_STATE = {
	ASSIGNED: 'assigned',
	SUGGESTED: 'suggested',
	UNASSIGNED: 'unassigned',
	PENDING_VALIDATION: 'pending_validation',
	PENDING_CREATION: 'pending_creation',
};

const LOCATION_PRESENTATION_LABEL = {
	[LOCATION_PRESENTATION_STATE.ASSIGNED]: 'Asignado en Synapse',
	[LOCATION_PRESENTATION_STATE.SUGGESTED]: 'Sugerencia',
	[LOCATION_PRESENTATION_STATE.UNASSIGNED]: 'Sin zona asignada',
	[LOCATION_PRESENTATION_STATE.PENDING_VALIDATION]:
		'Selección pendiente de validar',
	[LOCATION_PRESENTATION_STATE.PENDING_CREATION]: 'Nueva zona al confirmar',
};

const STATUS_ALIASES = {
	ready: REVIEW_STATUS.READY,
	exact: REVIEW_STATUS.READY,
	matched: REVIEW_STATUS.READY,
	success: REVIEW_STATUS.READY,
	adjusted: REVIEW_STATUS.AUTO_CORRECTED,
	auto_corrected: REVIEW_STATUS.AUTO_CORRECTED,
	auto_matched: REVIEW_STATUS.AUTO_CORRECTED,
	corrected: REVIEW_STATUS.AUTO_CORRECTED,
	review_required: REVIEW_STATUS.REVIEW_REQUIRED,
	needs_review: REVIEW_STATUS.REVIEW_REQUIRED,
	review: REVIEW_STATUS.REVIEW_REQUIRED,
	approval_available: REVIEW_STATUS.REVIEW_REQUIRED,
	warning_only: REVIEW_STATUS.REVIEW_REQUIRED,
	ambiguous: REVIEW_STATUS.BLOCKED,
	unresolved: REVIEW_STATUS.BLOCKED,
	blocked: REVIEW_STATUS.BLOCKED,
	unmatched: REVIEW_STATUS.BLOCKED,
	error: REVIEW_STATUS.BLOCKED,
};

const ISSUE_MESSAGES = {
	LOCATION_MATCH_NORMALIZED:
		'Ajustamos formato, tildes o puntuación de la ubicación.',
	LOCATION_MATCH_FUZZY:
		'Asignamos la zona más probable. Revísala si el dato del archivo es inusual.',
	LOCATION_MATCH_FUZZY_LOW_SIMILARITY:
		'Synapse asignó la zona más cercana, aunque la similitud fue baja. Revísala antes de importar.',
	LOCATION_MATCH_FUZZY_AMBIGUOUS:
		'Synapse eligió la primera entre varias zonas con similitud casi igual. Puedes cambiarla antes de importar.',
	LOCATION_MATCH_REVIEW_REQUIRED:
		'La sugerencia más cercana tiene similitud moderada. Revísala y aplícala manualmente.',
	LOCATION_MATCH_AMBIGUOUS:
		'Encontramos varias zonas con similitud casi igual. Elige la ubicación correcta.',
	LOCATION_MATCH_UNRESOLVED:
		'La similitud con las zonas registradas es demasiado baja para autocorregir. Revisa las sugerencias.',
	LOCATION_MISSING_ZONE_FALLBACK:
		'El Excel no indicó zona y Synapse eligió una alternativa del mismo cantón. Puedes cambiarla antes de importar.',
	LOCATION_MISSING_CITY_INFERRED:
		'El Excel no indicó ciudad y Synapse la infirió porque el cantón y la zona identifican una única ruta territorial.',
	LOCATION_ASSIGNED_PATH_INCOMPLETE:
		'Synapse no pudo completar ciudad, cantón y zona. Selecciona una ubicación completa.',
	ZONE_COVERAGE_PROMOTION_REQUIRED:
		'Hay un cambio de cobertura disponible. Revisa el plan antes de confirmar la importación.',
	ZONE_COVERAGE_APPROVAL_REQUIRED:
		'El plan de cobertura cambió. Revisa el detalle y confirma nuevamente la importación.',
	ZONE_COVERAGE_TECH_INELIGIBLE:
		'En este departamento no se modificará la cobertura del técnico.',
	ZONE_COVERAGE_AUZ_MISSING:
		'No se encontró una asignación AUZ reutilizable; no se modificará la cobertura.',
	ZONE_COVERAGE_AUZ_TYPE_INVALID:
		'El tipo AUZ no puede normalizarse automáticamente; no se modificará la cobertura.',
	ZONE_COVERAGE_SERVICE_INVALID:
		'El servicio no permite aprovisionar esta cobertura. Esta advertencia no cambia la asignación actual.',
	ZONE_COVERAGE_PROVISION_AVAILABLE:
		'Al confirmar se aplicarán los cambios provisionales de cobertura mostrados para este técnico y zona.',
	ZONE_COVERAGE_TECH_NOT_PROVISIONABLE:
		'No se modificará la cobertura porque el técnico o la entidad principal no admite aprovisionamiento automático. Esta advertencia no cambia la asignación actual.',
	ZONE_COVERAGE_AUZ_REQUIRES_BACKOFFICE:
		'No se modificará la cobertura y esta advertencia no cambia la asignación actual. La asignación AUZ está inactiva o tiene un tipo no normal y requiere una gestión futura de backoffice.',
	ZONE_COVERAGE_PROVISION_FAILED:
		'No fue posible aplicar el aprovisionamiento de cobertura. La orden no se confirmó y puedes revisar el plan antes de reintentar.',
	ORDER_ASSIGNMENT_CHANGED_SINCE_REVIEW:
		'La asignación cambió durante la validación final. Revisa el técnico anterior y el nuevo antes de confirmar otra vez.',
	ASSIGNMENT_CHANGED_SINCE_REVIEW:
		'La asignación cambió durante la validación final. Revisa el técnico anterior y el nuevo antes de confirmar otra vez.',
	ASSIGNMENT_WORKCODE_NOT_FOUND:
		'El identificador de técnico del Excel no corresponde a un usuario asignable en este departamento.',
	ORDER_ASSIGNMENT_WORKCODE_NOT_FOUND:
		'El identificador de técnico del Excel no corresponde a un usuario asignable en este departamento.',
	ASSIGNMENT_WORKCODE_AMBIGUOUS:
		'El identificador de técnico corresponde a más de un usuario. Synapse no elegirá uno de forma arbitraria.',
	ORDER_ASSIGNMENT_WORKCODE_AMBIGUOUS:
		'El identificador de técnico corresponde a más de un usuario. Synapse no elegirá uno de forma arbitraria.',
	ASSIGNMENT_RESET_FOR_CANCELLATION:
		'La cancelación restablecerá la asignación a la entidad principal. El cambio se muestra antes de importar.',
	ORDER_ASSIGNMENT_CHANGED_DURING_IMPORT:
		'La asignación cambió mientras se guardaba esta orden. No se confirmó y debe revalidarse.',
	INVALID_DATE: 'La fecha no coincide con el formato seleccionado.',
	DATE_DEFAULTED_TO_NOW:
		'La fecha venía vacía o inválida y Synapse usó el momento de procesamiento.',
	STATE_NOT_FOUND: 'El estado indicado no existe en Synapse. Corrígelo.',
	SERVICE_NOT_FOUND:
		'El servicio indicado no existe para este departamento. Corrígelo.',
	SERVICE_MATCH_NORMALIZED:
		'Synapse normalizó el nombre y encontró un servicio único para este alcance.',
	SERVICE_MATCH_FUZZY:
		'Synapse seleccionó automáticamente el servicio con la coincidencia más segura.',
	SERVICE_AUTO_MATCHED:
		'Synapse encontró y seleccionó automáticamente el servicio más seguro para este alcance.',
	SERVICE_MATCH_REVIEW_REQUIRED:
		'La coincidencia no es suficientemente segura. Selecciona el servicio correcto.',
	SERVICE_MATCH_AMBIGUOUS:
		'Hay más de un servicio posible. Selecciona el correcto antes de importar.',
	SERVICE_MATCH_UNRESOLVED:
		'No fue posible resolver el servicio. Selecciónalo manualmente o crea uno desde un arquetipo.',
	SERVICE_CREATION_AVAILABLE:
		'Hay un plan disponible para crear el servicio desde un arquetipo. Revísalo antes de confirmar.',
	SERVICE_CREATION_APPROVAL_REQUIRED:
		'El plan de creación del servicio cambió. Revísalo y confirma nuevamente.',
	CONTRACT_FREQUENCY_UNKNOWN:
		'La frecuencia del archivo no existe en el catálogo contractual. Corrígela en el archivo o créala antes de importar.',
	CONTRACT_FREQUENCY_AMBIGUOUS:
		'La frecuencia del archivo coincide con varias opciones. Selecciona la correcta.',
	CONTRACT_FREQUENCY_INACTIVE:
		'La frecuencia indicada está inactiva y no puede asignarse.',
	CONTRACT_FREQUENCY_REVIEW_REQUIRED:
		'La frecuencia del contrato difiere de la solicitada en el archivo. Confirma el cambio antes de importar.',
	CONTRACT_FREQUENCY_CONFLICT:
		'Hay frecuencias incompatibles para el mismo contrato. Corrige el lote antes de importar.',
	LEGACY_MAPPING_ERROR:
		'La orden contiene un conflicto de mapeo. Revisa sus campos.',
};

const REASON_MESSAGES = {
	existing_order: 'Se conservó la ubicación de la orden existente.',
	duplicate_complete_path:
		'Hay varias zonas registradas con la misma ruta territorial.',
	format_normalized:
		'Se ignoraron diferencias de formato, tildes o puntuación.',
	similarity_match: 'Se eligió la ruta territorial más parecida.',
	low_similarity_auto_selected:
		'Se seleccionó automáticamente la opción más cercana, aunque su similitud fue baja.',
	ambiguous_top_match_auto_selected:
		'Varias opciones tuvieron similitud casi igual; se eligió la primera de forma determinista.',
	empty_eligible_catalog: 'No hay zonas disponibles para este alcance.',
	zone_id_not_eligible:
		'La zona indicada no está disponible para este alcance.',
	zone_id_hierarchy_conflict:
		'La zona indicada no corresponde a la ciudad y el cantón del archivo.',
	incomplete_location_path:
		'Faltan ciudad, cantón o zona para realizar la comparación.',
	input_too_long:
		'La ubicación contiene un valor demasiado largo. Corrígelo en el archivo.',
	catalog_limit_exceeded:
		'El catálogo de zonas es demasiado grande para validar esta importación de forma segura.',
	catalog_value_too_long:
		'El catálogo contiene una ubicación demasiado larga para compararla de forma segura.',
	short_value_requires_exact:
		'Uno de los nombres es demasiado corto para sugerir una coincidencia aproximada.',
	component_threshold_not_met:
		'Ciudad, cantón o zona difieren demasiado de la mejor alternativa.',
	fuzzy_candidate_limit_exceeded:
		'Hay demasiadas alternativas posibles. Selecciona la zona manualmente.',
	insufficient_candidate_margin:
		'Las mejores alternativas tienen una similitud demasiado cercana.',
	below_auto_match_threshold:
		'La sugerencia más cercana tiene similitud insuficiente para autocorregir.',
	no_safe_candidate:
		'Ninguna alternativa alcanzó una similitud suficientemente segura.',
	missing_match: 'No se recibió un resultado de coincidencia.',
	missing_zone_parent_fallback:
		'El Excel no indicó zona; Synapse eligió una alternativa registrada en el mismo cantón.',
	missing_zone_parent_not_eligible:
		'El Excel no indicó zona y no hay alternativas registradas para esa ciudad y cantón.',
	missing_city_unique_township_zone:
		'La ciudad se infirió porque el cantón y la zona corresponden a una única ruta territorial.',
	missing_city_township_zone_ambiguous:
		'El cantón y la zona corresponden a más de una ciudad. Selecciona la ruta territorial completa.',
	missing_city_township_zone_not_found:
		'No se encontró una ruta territorial para el cantón y la zona sin una ciudad.',
	administrator_selected_new_zone:
		'Un administrador solicitó crear y seleccionar esta zona al confirmar la importación.',
	diacritics_removed: 'Se ignoraron tildes y otros signos diacríticos.',
	punctuation_removed: 'Se ignoraron signos de puntuación.',
	whitespace_normalized: 'Se normalizaron espacios.',
	case_normalized: 'Se ignoraron diferencias entre mayúsculas y minúsculas.',
	hierarchical_match: 'La coincidencia respeta la jerarquía territorial.',
};

const FIELD_LABELS = {
	cityId: 'la ciudad',
	townshipId: 'el cantón',
	contractZoneId: 'la zona de contrato',
	orderZoneId: 'la zona de orden',
	serviceId: 'el servicio',
	contractFrequencyId: 'la frecuencia contractual',
	stateId: 'el estado',
};

const REQUIRED_FIELD_LABELS = {
	clientIdc: 'ID Sitio del cliente',
	contractNumber: 'ID Sitio del contrato',
};

const asArray = value => (Array.isArray(value) ? value : []);
const asObject = value =>
	value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const IMPORT_ROW_ID_FIELD = '__importRowId';

export const ZONE_COVERAGE_ACTION = {
	CREATE_AUZ: 'create_auz',
	REACTIVATE_AUZ: 'reactivate_auz',
	NORMALIZE_AUZ_TYPE: 'normalize_auz_type',
	CREATE_AZD: 'create_azd',
	REACTIVATE_AZD: 'reactivate_azd',
};

const ZONE_COVERAGE_ACTIONS = new Set(Object.values(ZONE_COVERAGE_ACTION));

const SERVICE_ISSUES_RESOLVED_BY_CREATION = new Set([
	'SERVICE_NOT_FOUND',
	'SERVICE_MATCH_REVIEW_REQUIRED',
	'SERVICE_MATCH_AMBIGUOUS',
	'SERVICE_MATCH_UNRESOLVED',
]);

export const isServiceIssueResolvedByCreation = issue =>
	SERVICE_ISSUES_RESOLVED_BY_CREATION.has(String(issue?.code || ''));

const hasValue = value =>
	value !== undefined && value !== null && String(value).trim() !== '';

const normalizeServiceOption = value => {
	const option = asObject(value);
	const id = firstValue(
		option.id,
		option.serviceId,
		option.service_id,
		option.archetypeServiceId,
	);
	const name = firstValue(option.name, option.serviceName, option.service_name);
	if (!hasValue(id) || !hasValue(name)) return;
	const confidence = firstValue(
		option.confidence,
		option.score,
		option.similarity,
	);
	return {
		...option,
		id,
		name: String(name),
		...(hasValue(confidence) ? { confidence: Number(confidence) } : {}),
	};
};

const getVisibleServiceName = value =>
	String(value || '')
		.normalize('NFKC')
		.trim()
		.replace(/\s+/g, ' ');

const getVisibleServiceNameKey = value =>
	getVisibleServiceName(value).toLocaleLowerCase();

export const getServiceOptionDisplayList = (options = []) => {
	const normalizedOptions = asArray(options)
		.map(normalizeServiceOption)
		.filter(Boolean);
	const serviceIdsByName = normalizedOptions.reduce((result, option) => {
		const key = getVisibleServiceNameKey(option.name);
		if (!result.has(key)) result.set(key, new Set());
		result.get(key).add(String(option.id));
		return result;
	}, new Map());

	return normalizedOptions.map(option => {
		const visibleName = getVisibleServiceName(option.name);
		const hasNameCollision =
			serviceIdsByName.get(getVisibleServiceNameKey(option.name))?.size > 1;
		return {
			...option,
			displayName: hasNameCollision
				? `${visibleName} · ID ${option.id}`
				: visibleName,
			hasNameCollision,
		};
	});
};

const getFiniteServiceConfidence = option => {
	if (
		option?.confidence === null ||
		option?.confidence === undefined ||
		option?.confidence === ''
	)
		return;
	const confidence = Number(option.confidence);
	return Number.isFinite(confidence) ? confidence : undefined;
};

const compareServiceOptionsByName = (left, right) => {
	const nameOrder = getVisibleServiceNameKey(left.name).localeCompare(
		getVisibleServiceNameKey(right.name),
	);
	if (nameOrder) return nameOrder;
	const leftId = Number(left.id);
	const rightId = Number(right.id);
	if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId;
	return String(left.id).localeCompare(String(right.id));
};

export const getServiceSimilarityDisplayList = (options = []) =>
	getServiceOptionDisplayList(options).sort((left, right) => {
		const leftConfidence = getFiniteServiceConfidence(left);
		const rightConfidence = getFiniteServiceConfidence(right);
		const leftHasConfidence = leftConfidence !== undefined;
		const rightHasConfidence = rightConfidence !== undefined;
		if (
			leftHasConfidence &&
			rightHasConfidence &&
			leftConfidence !== rightConfidence
		)
			return rightConfidence - leftConfidence;
		if (leftHasConfidence !== rightHasConfidence)
			return leftHasConfidence ? -1 : 1;
		return compareServiceOptionsByName(left, right);
	});

export const getServiceCatalogDisplayList = (options = []) =>
	getServiceOptionDisplayList(options).sort(compareServiceOptionsByName);

const getZoneFields = locationFields => {
	if (Array.isArray(locationFields?.zoneFields))
		return locationFields.zoneFields;
	return locationFields?.zoneIdField ? [locationFields.zoneIdField] : [];
};

const EDITABLE_ZONE_FIELD_DESCRIPTORS = {
	contractZoneId: { id: 'contractZoneId', name: 'Zona de contrato' },
	orderZoneId: {
		id: 'orderZoneId',
		name: 'Zona de la orden',
		importReviewSynthetic: true,
	},
};

export const getEditableZoneFields = ({
	templateValidatorFields = {},
	orders = [],
} = {}) => {
	const templateFields = {
		contractZoneId: templateValidatorFields.contractZoneIdField,
		orderZoneId: templateValidatorFields.orderZoneIdField,
	};
	const operationalFieldIds = asArray(orders).reduce((fieldIds, order) => {
		const matches = asObject(getImportDiagnostics(order).locationMatches);
		const legacyFieldId = getImportDiagnostics(order).locationMatch?.field;
		Object.keys(EDITABLE_ZONE_FIELD_DESCRIPTORS).forEach(fieldId => {
			if (
				Object.prototype.hasOwnProperty.call(order || {}, fieldId) ||
				Object.prototype.hasOwnProperty.call(matches, fieldId) ||
				String(legacyFieldId || '') === fieldId
			)
				fieldIds.add(fieldId);
		});
		return fieldIds;
	}, new Set());

	return ['contractZoneId', 'orderZoneId']
		.filter(
			fieldId =>
				templateFields[fieldId] ||
				(fieldId === 'orderZoneId' && operationalFieldIds.has(fieldId)),
		)
		.map(
			fieldId =>
				templateFields[fieldId] || EDITABLE_ZONE_FIELD_DESCRIPTORS[fieldId],
		);
};

const getLocationFields = (fields = [], locationFields = {}) => {
	const safeFields = asArray(fields);
	const findField = id => safeFields.find(field => field?.id === id);
	const explicitZoneFields = getZoneFields(locationFields);
	const inferredZoneFields = ['contractZoneId', 'orderZoneId', 'zoneId']
		.map(findField)
		.filter(Boolean);

	return {
		cityIdField: locationFields.cityIdField || findField('cityId'),
		townshipIdField: locationFields.townshipIdField || findField('townshipId'),
		zoneFields: explicitZoneFields.length
			? explicitZoneFields
			: inferredZoneFields,
	};
};

const getEffectiveLocationMatches = diagnostics => {
	const matches = asObject(diagnostics?.locationMatches);
	if (Object.keys(matches).length) return matches;

	const legacyMatch = asObject(diagnostics?.locationMatch);
	if (!Object.keys(legacyMatch).length) return {};
	return { [legacyMatch.field || 'zone']: legacyMatch };
};

const getZoneSourceKey = fieldId => {
	if (fieldId === 'contractZoneId') return 'contractZone';
	if (fieldId === 'orderZoneId') return 'orderZone';
	return 'zone';
};

export const getImportDiagnostics = order => asObject(order?.importDiagnostics);

export const getApplicableZoneFields = (order, zoneFields = []) => {
	const diagnostics = getImportDiagnostics(order);
	const matches = asObject(diagnostics.locationMatches);
	const legacyFieldId = diagnostics.locationMatch?.field;
	const issues = asArray(diagnostics.issues);
	const sourceValues = asObject(diagnostics.sourceValues);

	return asArray(zoneFields).filter(field => {
		if (field?.id !== 'orderZoneId') return true;

		const match = asObject(matches[field.id]);
		const hasCapturedSource = Object.prototype.hasOwnProperty.call(
			sourceValues,
			'orderZone',
		);
		const hasSubmittedSource = hasValue(sourceValues.orderZone);
		const hasManualSelection =
			['manual', 'manual_pending_validation'].includes(match.strategy) &&
			hasValue(match.assigned?.zoneId);
		const hasSignedCreation = hasValue(
			match.zoneCreation?.eligibilityChallenge,
		);
		const hasRequiredIssue = issues.some(
			issue =>
				issue?.code === 'REQUIRED_FIELD_MISSING' &&
				String(issue?.field || '') === field.id,
		);
		const blankDerivedMatch =
			hasCapturedSource &&
			!hasSubmittedSource &&
			match.strategy === 'parent_fallback' &&
			!hasManualSelection &&
			!hasSignedCreation;
		const hasCanonicalSelection =
			!blankDerivedMatch &&
			(hasValue(order?.[field.id]) || hasValue(order?.orderZone?.zoneId));
		const hasExistingOverride =
			hasValue(order?.exists?.[field.id]) ||
			hasValue(order?.exists?.orderZone?.zoneId);
		const hasResolverEvidence =
			(!hasCapturedSource || hasSubmittedSource) &&
			(Object.prototype.hasOwnProperty.call(matches, field.id) ||
				String(legacyFieldId || '') === field.id);

		return (
			field.required === true ||
			hasSubmittedSource ||
			hasCanonicalSelection ||
			hasExistingOverride ||
			hasManualSelection ||
			hasSignedCreation ||
			hasRequiredIssue ||
			hasResolverEvidence
		);
	});
};

const FIELD_REVIEW_ISSUES = new Set(['REQUIRED_FIELD_MISSING', 'INVALID_DATE']);
const FIELD_REVIEW_DATE_INPUT_FORMAT = 'YYYY-MM-DD';
const FIELD_REVIEW_DATE_WIRE_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

const isStrictFieldReviewDate = (value, timezone) => {
	if (typeof value !== 'string' || !moment.tz.zone(timezone)) return false;
	const parsed = moment.tz(
		value,
		FIELD_REVIEW_DATE_INPUT_FORMAT,
		true,
		timezone,
	);
	return (
		parsed.isValid() && parsed.format(FIELD_REVIEW_DATE_INPUT_FORMAT) === value
	);
};

const getCanonicalFieldReviewDate = (value, editor) => {
	if (typeof value !== 'string') return;
	const parsed = moment.utc(value, editor.wireFormat, true);
	if (!parsed.isValid() || parsed.format(editor.wireFormat) !== value) return;
	const input = parsed.tz(editor.timezone).format(editor.inputFormat);
	const canonical = moment
		.tz(input, editor.inputFormat, true, editor.timezone)
		.utc()
		.format(editor.wireFormat);
	if (canonical !== value) return;
	return { input, value: canonical };
};

const normalizeDateReviewEditor = (value, constraints) => {
	if (
		!['date', 'datetime'].includes(value.type) ||
		value.kind !== 'date' ||
		constraints.required !== true ||
		constraints.inputFormat !== FIELD_REVIEW_DATE_INPUT_FORMAT ||
		constraints.wireFormat !== FIELD_REVIEW_DATE_WIRE_FORMAT ||
		!moment.tz.zone(constraints.timezone)
	)
		return;
	const min = constraints.min == null ? undefined : String(constraints.min);
	const max = constraints.max == null ? undefined : String(constraints.max);
	if (
		(min && !isStrictFieldReviewDate(min, constraints.timezone)) ||
		(max && !isStrictFieldReviewDate(max, constraints.timezone)) ||
		(min && max && min > max)
	)
		return;
	return {
		inputFormat: FIELD_REVIEW_DATE_INPUT_FORMAT,
		wireFormat: FIELD_REVIEW_DATE_WIRE_FORMAT,
		timezone: constraints.timezone,
		min,
		max,
	};
};

const normalizeFieldReviewEditor = editor => {
	const value = asObject(editor);
	const constraints = asObject(value.constraints);
	const configuredMaxLength = Number(constraints.maxLength);
	if (
		!hasValue(value.fieldId) ||
		!hasValue(value.label) ||
		value.scope !== 'new_order_only' ||
		!FIELD_REVIEW_ISSUES.has(value.issueCode) ||
		!['required', 'resolved'].includes(value.status) ||
		!hasValue(value.selectionChallenge)
	)
		return;
	const dateConstraints = normalizeDateReviewEditor(value, constraints);
	const isScalar = value.type === 'string' && value.kind === 'scalar';
	if (!isScalar && !dateConstraints) return;
	if (isScalar && value.issueCode !== 'REQUIRED_FIELD_MISSING') return;
	return {
		fieldId: String(value.fieldId),
		label: String(value.label),
		type: value.type,
		kind: value.kind,
		scope: value.scope,
		issueCode: value.issueCode,
		status: value.status,
		sourceCategory: value.sourceCategory,
		selectionChallenge: String(value.selectionChallenge),
		value: value.value == null ? '' : String(value.value),
		required: constraints.required === true,
		...(isScalar
			? {
					maxLength:
						Number.isInteger(configuredMaxLength) && configuredMaxLength > 0
							? Math.min(configuredMaxLength, 100)
							: 100,
				}
			: dateConstraints),
	};
};

export const getFieldReviewDescriptors = order => {
	const diagnostics = getImportDiagnostics(order);
	const fieldReview = asObject(diagnostics.fieldReview);
	if (fieldReview.version !== 1) return [];
	return asArray(fieldReview.editors)
		.map(normalizeFieldReviewEditor)
		.filter(Boolean);
};

export const getFieldReviewEditors = order => {
	const diagnostics = getImportDiagnostics(order);
	const issues = asArray(diagnostics.issues);
	return getFieldReviewDescriptors(order)
		.filter(editor => editor.status === 'required')
		.filter(editor =>
			issues.some(
				issue =>
					String(issue?.code || '') === editor.issueCode &&
					String(issue?.field || '') === editor.fieldId,
			),
		);
};

export const validateFieldReviewValue = (value, editor) => {
	if (editor?.kind === 'date') {
		const rawValue = String(value ?? '').trim();
		if (!rawValue)
			return {
				valid: editor.required !== true,
				reason: editor.required === true ? 'required' : undefined,
				value: '',
			};
		const canonicalInput = getCanonicalFieldReviewDate(rawValue, editor);
		const input = canonicalInput?.input || rawValue;
		if (!canonicalInput && !isStrictFieldReviewDate(input, editor.timezone))
			return { valid: false, reason: 'format', value: '' };
		if (editor.min && input < editor.min)
			return { valid: false, reason: 'min', value: '' };
		if (editor.max && input > editor.max)
			return { valid: false, reason: 'max', value: '' };
		return {
			valid: true,
			value:
				canonicalInput?.value ||
				moment
					.tz(input, editor.inputFormat, true, editor.timezone)
					.utc()
					.format(editor.wireFormat),
		};
	}
	const normalized = String(value ?? '')
		.replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
		.trim()
		.slice(0, Number(editor?.maxLength) || 100);
	return {
		valid: editor?.required !== true || Boolean(normalized),
		reason: editor?.required === true && !normalized ? 'required' : undefined,
		value: normalized,
	};
};

export const normalizeFieldReviewValue = (value, editor) =>
	validateFieldReviewValue(value, editor).value;

export const getFieldReviewInputValue = (value, editor) => {
	const input = String(value ?? '').trim();
	if (editor?.kind !== 'date') return input;
	if (isStrictFieldReviewDate(input, editor.timezone)) return input;
	return getCanonicalFieldReviewDate(input, editor)?.input || '';
};

export const getFieldReviewSelectionsPayload = selections =>
	asArray(selections)
		.filter(selection => hasValue(selection?.challenge))
		.map(selection => ({
			challenge: String(selection.challenge),
			value: String(selection.value ?? ''),
		}));

export const getFieldReviewSnapshot = response =>
	asArray(asObject(response).fieldReviewSnapshot).filter(
		proof =>
			hasValue(proof?.rowId) &&
			hasValue(proof?.fieldId) &&
			FIELD_REVIEW_ISSUES.has(proof?.issueCode) &&
			hasValue(proof?.reviewChallenge),
	);

export const hasSafeFieldReviewSnapshot = (
	snapshot,
	selections,
	{ requireExact = false } = {},
) => {
	const proofs = asArray(snapshot);
	const pending = asArray(selections);
	if (!pending.length) return true;
	return (
		(!requireExact || proofs.length === pending.length) &&
		pending.every(selection => {
			const matches = proofs.filter(
				proof =>
					String(proof?.rowId) === String(selection?.rowId) &&
					String(proof?.fieldId) === String(selection?.fieldId) &&
					proof?.issueCode === selection?.issueCode &&
					String(proof?.value ?? '') === String(selection?.value ?? '') &&
					hasValue(proof?.reviewChallenge),
			);
			return matches.length === 1;
		})
	);
};

/**
 * Stable adapter for the service resolver contract. Keeping the wire aliases in
 * one place prevents the review UI from depending on a particular API casing.
 */
export const isExistingImportOrder = order => hasValue(order?.exists?.id);

export const getServicePresentation = order => {
	const diagnostics = getImportDiagnostics(order);
	const match = asObject(diagnostics.serviceMatch || diagnostics.service_match);
	const isExisting = isExistingImportOrder(order);
	// Only source-only legacy diagnostics lack an authoritative matching result.
	const hasMatchingResult = [
		'assigned',
		'status',
		'strategy',
		'candidates',
		'needsReview',
		'needs_review',
	].some(key => Object.prototype.hasOwnProperty.call(match, key));
	const assigned = normalizeServiceOption(
		isExisting
			? order.exists.service
			: hasMatchingResult
				? match.assigned
				: order?.service,
	);
	const candidates = asArray(match.candidates)
		.map(normalizeServiceOption)
		.filter(Boolean);
	const source = asObject(match.source);
	const status = String(match.status || '').toLowerCase();
	const strategy = String(match.strategy || '').toLowerCase();
	return {
		field: match.field || 'serviceId',
		source: Object.prototype.hasOwnProperty.call(source, 'value')
			? source.value
			: match.source !== undefined
				? match.source
				: diagnostics.sourceValues?.service,
		activityType: firstValue(
			match.activityType,
			match.activity_type,
			match.activity?.type,
			match.activity?.name,
			diagnostics.sourceValues?.activityType,
		),
		isExisting,
		status: isExisting ? 'existing' : status,
		strategy: isExisting ? 'existing' : strategy,
		assigned,
		allowManualSelection: !isExisting && match.allowManualSelection === true,
		confidence: isExisting
			? undefined
			: firstValue(match.confidence, match.score, assigned?.confidence),
		reasons: isExisting ? [] : asArray(match.reasons),
		candidates: isExisting ? [] : candidates,
		cohortKey: firstValue(match.cohortKey, match.cohort_key),
		needsReview:
			!isExisting &&
			(match.needsReview === true ||
				match.needs_review === true ||
				['ambiguous', 'unresolved', 'review_required'].includes(status) ||
				['ambiguous', 'unresolved'].includes(strategy)),
		previous: isExisting ? {} : asObject(match.previous),
	};
};

export const getServiceCohortKey = order => {
	const service = getServicePresentation(order);
	const hasSource = hasValue(service.source);
	const source = hasSource
		? String(service.source).trim().toLocaleLowerCase()
		: '<blank>';
	// Blank service values are never an implicit cohort. Even if an older API
	// response grouped them by activity, targeting stays row-scoped until the
	// administrator explicitly supplies exact row IDs.
	if (!hasSource) {
		const rowId = getImportRowIdentity(order) || order?.odtId || order?.id;
		return `service:<blank>:row:${rowId || 'unknown'}`;
	}
	if (hasValue(service.cohortKey)) return String(service.cohortKey);
	const activityType = hasValue(service.activityType)
		? String(service.activityType).trim().toLocaleLowerCase()
		: '<unknown-activity>';
	return `service:${activityType}:${source}`;
};

export const getServiceCohortTargets = (orders, sourceOrder) => {
	if (isExistingImportOrder(sourceOrder)) return [];
	const sourceService = getServicePresentation(sourceOrder);
	if (!hasValue(sourceService.source)) return [sourceOrder].filter(Boolean);
	const cohortKey = getServiceCohortKey(sourceOrder);
	return asArray(orders).filter(order => {
		const service = getServicePresentation(order);
		return (
			!service.isExisting &&
			hasValue(service.source) &&
			getServiceCohortKey(order) === cohortKey
		);
	});
};

export const getServiceCatalog = order => {
	const diagnostics = getImportDiagnostics(order);
	const serviceCreation = asObject(diagnostics.serviceCreation);
	const sources = [
		...asArray(diagnostics.serviceCatalog),
		...asArray(diagnostics.service_catalog),
		...asArray(serviceCreation.archetypes),
		...getServicePresentation(order).candidates,
	];
	const byId = new Map();
	sources
		.map(normalizeServiceOption)
		.filter(Boolean)
		.forEach(option => {
			if (!byId.has(String(option.id))) byId.set(String(option.id), option);
		});
	return Array.from(byId.values());
};

export const getServiceArchetypeCatalog = order => {
	const diagnostics = getImportDiagnostics(order);
	return asArray(diagnostics.serviceCreation?.archetypes)
		.map(normalizeServiceOption)
		.filter(Boolean);
};

export const applyManualServiceSelection = ({
	order,
	option,
	serviceIdField,
}) => {
	const normalizedOption = normalizeServiceOption(option);
	if (isExistingImportOrder(order) || !normalizedOption || !serviceIdField)
		return order;
	const selectedConfidence = hasValue(normalizedOption.confidence)
		? normalizedOption.confidence
		: null;
	const diagnostics = getImportDiagnostics(order);
	const current = getServicePresentation(order);
	return {
		...order,
		[serviceIdField.id]: normalizedOption.id,
		[serviceIdField.name]: normalizedOption.name,
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			serviceMatch: {
				...asObject(diagnostics.serviceMatch),
				field: serviceIdField.id,
				strategy: 'manual',
				assigned: {
					id: normalizedOption.id,
					name: normalizedOption.name,
					...(selectedConfidence !== null
						? { confidence: selectedConfidence }
						: {}),
				},
				confidence: selectedConfidence,
				score: selectedConfidence,
				needsReview: true,
				cohortKey: getServiceCohortKey(order),
				previous: {
					id: order?.[serviceIdField.id] ?? null,
					name: order?.[serviceIdField.name] ?? null,
				},
			},
		},
	};
};

export const undoManualServiceSelection = ({ order, serviceIdField }) => {
	if (isExistingImportOrder(order) || !serviceIdField) return order;
	const diagnostics = getImportDiagnostics(order);
	const match = asObject(diagnostics.serviceMatch);
	const previous = asObject(match.previous);
	if (!Object.keys(previous).length) return order;
	const restoredMatch = { ...match };
	delete restoredMatch.previous;
	delete restoredMatch.assigned;
	restoredMatch.strategy = 'unresolved';
	restoredMatch.needsReview = true;
	return {
		...order,
		[serviceIdField.id]: previous.id ?? null,
		[serviceIdField.name]: previous.name ?? null,
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			serviceMatch: restoredMatch,
		},
	};
};

export const applyServiceCreationRequest = ({
	order,
	name,
	archetypeServiceId,
	targetRowIds,
}) => {
	if (isExistingImportOrder(order)) return order;
	const normalizedName = String(name || '').trim();
	if (!normalizedName)
		throw new Error('Escribe un nombre para el nuevo servicio.');
	const diagnostics = getImportDiagnostics(order);
	const creationPolicy = asObject(diagnostics.serviceCreation);
	if (
		creationPolicy.allowed !== true &&
		creationPolicy.canCreate !== true &&
		diagnostics.serviceMatch?.canCreate !== true
	)
		throw new Error(
			'No hay autorización para crear servicios en este alcance.',
		);
	const archetype = getServiceArchetypeCatalog(order).find(
		option => String(option.id) === String(archetypeServiceId),
	);
	if (!archetype)
		throw new Error('Selecciona un arquetipo disponible para este alcance.');
	const authoritativeCohortKey = firstValue(
		getServicePresentation(order).cohortKey,
		getServiceCohortKey(order),
	);
	const currentRowId = getImportRowIdentity(order) || order?.odtId || order?.id;
	const explicitTargetRowIds = Array.from(
		new Set(
			(asArray(targetRowIds).length ? targetRowIds : [currentRowId])
				.filter(hasValue)
				.map(String),
		),
	).sort();
	if (
		!explicitTargetRowIds.length ||
		!explicitTargetRowIds.includes(String(currentRowId))
	)
		throw new Error('La selección explícita debe incluir la fila actual.');
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			serviceMatch: {
				...asObject(diagnostics.serviceMatch),
				strategy: 'create_new',
				needsReview: true,
				cohortKey: authoritativeCohortKey,
			},
			serviceCreation: {
				request: {
					name: normalizedName,
					archetypeServiceId: archetype.id,
					cohortKey: authoritativeCohortKey,
					targetRowIds: explicitTargetRowIds,
				},
			},
		},
	};
};

export const clearServiceCreationRequest = order => {
	const diagnostics = getImportDiagnostics(order);
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			serviceCreation: {},
		},
	};
};

const normalizeServiceCreation = value => {
	const creation = asObject(value);
	if (
		!['available', 'selected'].includes(
			String(creation.status || '').toLowerCase(),
		)
	)
		return;
	const challenge = firstValue(
		creation.challenge,
		creation.reviewChallenge,
		creation.eligibilityChallenge,
	);
	const targetRowIds = asArray(creation.targetRowIds)
		.filter(hasValue)
		.map(String);
	if (
		!hasValue(challenge) ||
		!asObject(creation.plan).name ||
		!targetRowIds.length
	)
		return;
	return {
		...creation,
		challenge: String(challenge),
		cohortKey: firstValue(creation.cohortKey, creation.cohort_key),
		targetRowIds,
		affectedCount: Number.isFinite(Number(creation.affectedCount))
			? Number(creation.affectedCount)
			: asArray(creation.targetRowIds).length,
	};
};

export const getServiceCreationOption = order =>
	isExistingImportOrder(order)
		? undefined
		: normalizeServiceCreation(getImportDiagnostics(order).serviceCreation);

export const getServiceCreationSelectionsFromOrders = orders => {
	const selections = new Map();
	asArray(orders).forEach(order => {
		const option = getServiceCreationOption(order);
		if (!option) return;
		if (!selections.has(option.challenge))
			selections.set(option.challenge, option);
	});
	return Array.from(selections.values());
};

export const isOrderTargetedByServiceCreation = (order, selection) => {
	const rowId = getImportRowIdentity(order);
	return (
		hasValue(rowId) &&
		asArray(selection?.targetRowIds).map(String).includes(String(rowId))
	);
};

export const setServiceCreationSelected = ({ order, selection, selected }) => {
	if (
		isExistingImportOrder(order) ||
		!isOrderTargetedByServiceCreation(order, selection)
	)
		return order;
	const diagnostics = getImportDiagnostics(order);
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			status: selected ? 'review_required' : diagnostics.status,
			issues: asArray(diagnostics.issues).map(issue =>
				isServiceIssueResolvedByCreation(issue)
					? { ...issue, blocking: selected ? false : true }
					: issue,
			),
			serviceCreation: {
				...asObject(diagnostics.serviceCreation),
				status: selected ? 'selected' : 'available',
			},
		},
	};
};

export const getServiceCreationSelectionsPayload = selections => {
	const byChallenge = new Map();
	asArray(selections).forEach(selection => {
		if (!hasValue(selection?.challenge)) return;
		const targetRowIds = asArray(selection?.targetRowIds)
			.filter(hasValue)
			.map(String);
		if (!targetRowIds.length) return;
		const challenge = String(selection.challenge);
		if (!byChallenge.has(challenge))
			byChallenge.set(challenge, { challenge, targetRowIds });
	});
	return Array.from(byChallenge.values()).sort((left, right) =>
		left.challenge.localeCompare(right.challenge),
	);
};

export const getServiceReviewSelectionsPayload = selections =>
	Array.from(
		new Set(
			asArray(selections)
				.map(selection => selection?.challenge)
				.filter(hasValue)
				.map(String),
		),
	)
		.sort()
		.map(challenge => ({ challenge }));

export const getAssignmentReviewSelectionsPayload = selections =>
	Array.from(
		new Set(
			asArray(selections)
				.map(selection => selection?.challenge)
				.filter(hasValue)
				.map(String),
		),
	)
		.sort()
		.map(challenge => ({ challenge }));

export const getServiceCreationPlanFingerprint = selections =>
	JSON.stringify(
		asArray(selections)
			.map(selection => ({
				cohortKey: selection?.cohortKey || null,
				targetRowIds: asArray(selection?.targetRowIds).map(String).sort(),
				name: selection?.plan?.name || null,
				archetypeServiceId:
					selection?.plan?.archetype?.id ||
					selection?.plan?.archetypeServiceId ||
					null,
			}))
			.sort((left, right) =>
				String(left.cohortKey).localeCompare(String(right.cohortKey)),
			),
	);

export const getImportRowIdentity = order =>
	order?.[IMPORT_ROW_ID_FIELD] || getImportDiagnostics(order).clientRowId;

const getContractFrequencySnapshotId = value => {
	const numericId = Number(value);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

export const getContractFrequencyReviewFingerprint = (
	orders,
	contractFrequencyIdField,
) =>
	JSON.stringify(
		asArray(orders)
			.map(order => {
				const frequency = getContractFrequencyPresentation(order);
				return {
					rowId: String(getImportRowIdentity(order) ?? ''),
					status: frequency.status || null,
					existingFrequencyId: getContractFrequencySnapshotId(
						frequency.existing?.frequencyId,
					),
					requestedFrequencyId: getContractFrequencySnapshotId(
						frequency.requested?.frequencyId,
					),
					assignedFrequencyId: getContractFrequencySnapshotId(
						frequency.assigned?.frequencyId,
					),
					contractFrequencyId: getContractFrequencySnapshotId(
						order?.[contractFrequencyIdField?.id || 'contractFrequencyId'],
					),
				};
			})
			.sort((left, right) => left.rowId.localeCompare(right.rowId)),
	);

export const assignImportRowIdentities = orders =>
	asArray(orders).map((order, index) => {
		const diagnostics = getImportDiagnostics(order);
		const existingIdentity = getImportRowIdentity(order);
		const clientRowId =
			existingIdentity ||
			`import-row-${diagnostics.rowNumber ?? 'unknown'}-${index}`;
		return {
			...order,
			[IMPORT_ROW_ID_FIELD]: clientRowId,
			importDiagnostics: {
				...diagnostics,
				clientRowId,
			},
		};
	});

const firstValue = (...values) => values.find(hasValue);

const normalizeAssignmentParty = value => {
	const party = asObject(value);
	if (!Object.keys(party).length) return;
	const normalized = {
		assignedTechId: firstValue(
			party.assignedTechId,
			party.assigned_tech_id,
			party.userId,
			party.user_id,
			party.id,
		),
		name: firstValue(
			party.name,
			party.assignedTechName,
			party.assigned_tech_name,
			party.userName,
			party.user_name,
		),
		workCode: firstValue(
			party.workCode,
			party.workcode,
			party.work_code,
			party.assignedTechWorkCode,
			party.assigned_tech_workcode,
		),
		entityId: firstValue(party.entityId, party.entity_id),
		entityName: firstValue(party.entityName, party.entity_name),
		supervisorId: firstValue(
			party.supervisorId,
			party.supervisor_id,
			party.supervisor?.supervisorId,
			party.supervisor?.id,
		),
		supervisorName: firstValue(
			party.supervisorName,
			party.supervisor_name,
			party.supervisor?.name,
		),
		supervisorWorkCode: firstValue(
			party.supervisorWorkCode,
			party.supervisor_workcode,
			party.supervisor?.workCode,
			party.supervisor?.work_code,
		),
	};
	return Object.keys(normalized).reduce((result, key) => {
		if (hasValue(normalized[key])) result[key] = normalized[key];
		return result;
	}, {});
};

const getOrderAssignmentFallback = (order, assignedTechIdField) => {
	const fallback = normalizeAssignmentParty({
		assignedTechId:
			(assignedTechIdField && order?.[assignedTechIdField.id]) ||
			order?.assignedTechId ||
			order?.assigned_tech_id,
		name:
			(assignedTechIdField && order?.[assignedTechIdField.name]) ||
			order?.assignedTechName ||
			order?.assigned_tech_name,
		workCode:
			order?.assignedTechWorkCode ||
			order?.assignedTechWorkcode ||
			order?.assigned_tech_workcode ||
			order?.workCode,
		entityId: order?.entityId || order?.entity_id,
		entityName: order?.entityName || order?.entity_name,
		supervisorId: order?.supervisorId || order?.supervisor_id,
		supervisorName: order?.supervisorName || order?.supervisor_name,
		supervisorWorkCode: order?.supervisorWorkCode || order?.supervisor_workcode,
	});
	return fallback && Object.keys(fallback).length ? fallback : undefined;
};

const isMainEntityAssignmentParty = party => {
	const normalized = normalizeAssignmentParty(party);
	return (
		hasValue(normalized?.assignedTechId) &&
		hasValue(normalized?.entityId) &&
		String(normalized.assignedTechId) === String(normalized.entityId)
	);
};

const hasMainEntityFallbackProvenance = assignment => {
	const strategy = String(assignment?.strategy || '').toLowerCase();
	const reason = String(assignment?.reason || '').toLowerCase();
	const hasMainEntityMarker = value =>
		/main[_ -]?entity|entidad principal|bucket principal/.test(value);
	const hasFallbackReason = value =>
		/unresolved|not[_ -]?found|unknown|ineligible|not[_ -]?assignable|ambiguous|policy[_ -]?rejected|no se pudo resolver|no (?:es|est[aá]) asignable|inelegible|ambigu|desconoc/.test(
			value,
		);
	return (
		(hasMainEntityMarker(strategy) && hasFallbackReason(strategy)) ||
		(hasMainEntityMarker(reason) && hasFallbackReason(reason))
	);
};

const hasNonBlockingAssignmentFallbackIssue = (
	assignment,
	diagnosticIssues = [],
) =>
	[...asArray(diagnosticIssues), ...asArray(assignment?.issues)].some(issue => {
		const code = String(issue?.code || '');
		return (
			issue?.blocking === false &&
			(/(^|_)ASSIGNMENT_/.test(code) || issue?.field === 'assignedTechId')
		);
	});

const isImportableMainEntityFallback = (assignment, diagnosticIssues = []) =>
	isMainEntityAssignmentParty(assignment?.resolved) &&
	hasMainEntityFallbackProvenance(assignment) &&
	hasNonBlockingAssignmentFallbackIssue(assignment, diagnosticIssues);

const getAssignmentExplanation = (assignment, diagnosticIssues) => {
	const strategy = String(assignment?.strategy || '').toLowerCase();
	const reason = String(assignment?.reason || '').toLowerCase();
	if (isImportableMainEntityFallback(assignment, diagnosticIssues)) {
		const resolved = normalizeAssignmentParty(assignment?.resolved);
		const mainEntityName = resolved?.entityName || resolved?.name;
		return `El técnico indicado en el Excel no se pudo validar como asignable; la orden se enviará al bucket principal${
			mainEntityName ? ` ${mainEntityName}` : ''
		}.`;
	}
	if (
		[
			'existing',
			'existing_order',
			'existing_authoritative',
			'preserve_existing',
		].includes(strategy) ||
		reason === 'existing_order'
	)
		return 'Synapse conservó la asignación actual.';
	if (
		strategy.includes('cancel') ||
		['main_entity_reset'].includes(strategy) ||
		reason.includes('cancel')
	)
		return 'La cancelación restablecerá la asignación a la entidad principal.';
	if (strategy.includes('ambiguous') || reason.includes('ambiguous'))
		return 'El identificador del Excel es ambiguo; Synapse no eligió un usuario de forma arbitraria.';
	if (
		strategy.includes('not_found') ||
		strategy === 'explicit_unresolved' ||
		strategy.includes('unknown') ||
		reason.includes('not_found') ||
		reason.includes('unknown')
	)
		return 'No se encontró el identificador del Excel; revisa la asignación resuelta por Synapse.';
	if (
		['explicit_workcode', 'explicit_workcode_name', 'manual_workcode'].includes(
			strategy,
		)
	)
		return 'Synapse resolvió el técnico por el identificador indicado en el Excel.';
	const detail = assignment?.reasonMessage || assignment?.reason;
	return hasValue(detail) ? String(detail) : undefined;
};

const normalizeAssignmentMutability = value => {
	const mutability = asObject(value);
	if (!Object.keys(mutability).length)
		return {
			allowed: false,
			reasonCode: 'ASSIGNMENT_MUTABILITY_UNKNOWN',
			message: undefined,
		};
	return {
		allowed: mutability.allowed === true,
		reasonCode:
			String(mutability.reasonCode || '').trim() ||
			(mutability.allowed === true
				? 'ASSIGNMENT_MUTABLE'
				: 'ASSIGNMENT_MUTABILITY_UNKNOWN'),
		message: hasValue(mutability.message)
			? String(mutability.message).trim()
			: undefined,
	};
};

const normalizeAssignmentCandidates = values =>
	asArray(values)
		.map(value => {
			const party = normalizeAssignmentParty(value);
			const challenge = hasValue(value?.selectionChallenge)
				? String(value.selectionChallenge).trim()
				: '';
			if (!hasValue(party?.assignedTechId) || !challenge) return;
			return {
				id: party.assignedTechId,
				assignedTechId: party.assignedTechId,
				...(hasValue(party.name) ? { name: party.name } : {}),
				...(hasValue(party.workCode) ? { workCode: party.workCode } : {}),
				...(hasValue(party.entityId) ? { entityId: party.entityId } : {}),
				...(hasValue(party.entityName) ? { entityName: party.entityName } : {}),
				...(hasValue(value?.divisionId)
					? { divisionId: value.divisionId }
					: {}),
				selectionChallenge: challenge,
			};
		})
		.filter(Boolean);

const ASSIGNMENT_MUTABILITY_MESSAGES = {
	ASSIGNMENT_FLOW_LOCKED:
		'El estado de la orden no permite elegir otro técnico en esta revisión. Este aviso solo limita la elección de técnico y no bloquea la importación; corrige los demás errores bloqueantes, si los hay.',
	ASSIGNMENT_BILLING_EXISTS:
		'La orden ya está facturada; la asignación no puede cambiarse desde esta importación.',
	ASSIGNMENT_EVENT_BILLED:
		'El evento actual ya está facturado; la asignación no puede cambiarse.',
	ASSIGNMENT_EVENT_FROZEN: 'El evento actual bloquea cambios de asignación.',
	ASSIGNMENT_PERMISSION_DENIED:
		'Tu perfil no tiene permiso para cambiar la asignación.',
	ASSIGNMENT_SCOPE_UNKNOWN:
		'No fue posible determinar el alcance de asignación para esta orden.',
	ASSIGNMENT_NO_CANDIDATES:
		'No hay técnicos elegibles para esta orden en el alcance autorizado.',
	ASSIGNMENT_MUTABILITY_UNKNOWN:
		'No fue posible confirmar si la asignación puede modificarse. Valida nuevamente la importación.',
};

export const getAssignmentMutabilityMessage = mutability =>
	mutability?.message ||
	ASSIGNMENT_MUTABILITY_MESSAGES[mutability?.reasonCode] ||
	'La API indicó que esta asignación no puede modificarse.';

export const getAssignmentPresentation = (order, assignedTechIdField) => {
	const diagnostics = getImportDiagnostics(order);
	const assignment = asObject(diagnostics.assignment);
	const fallback = getOrderAssignmentFallback(order, assignedTechIdField);
	const source = normalizeAssignmentParty(assignment.source);
	const existing = normalizeAssignmentParty(assignment.existing);
	const resolved = normalizeAssignmentParty(assignment.resolved) || fallback;
	const previousResolved = normalizeAssignmentParty(
		assignment.previousResolved || assignment.reviewed,
	);
	return {
		source,
		existing,
		resolved,
		previousResolved,
		strategy: assignment.strategy,
		reason: assignment.reason,
		mutability: normalizeAssignmentMutability(assignment.mutability),
		candidates: normalizeAssignmentCandidates(assignment.candidates),
		explanation: getAssignmentExplanation(assignment, diagnostics.issues),
	};
};

const toSnapshotId = value => {
	if (!hasValue(value)) return null;
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : value;
};

const toSnapshotWorkCode = value =>
	hasValue(value) ? String(value).trim() : null;

export const getAssignmentReviewSnapshot = (orders, assignedTechIdField) =>
	asArray(orders)
		.map(order => {
			const rowId = getImportRowIdentity(order);
			if (!hasValue(rowId)) return;
			const diagnostics = getImportDiagnostics(order);
			const resolved = getAssignmentPresentation(
				order,
				assignedTechIdField,
			).resolved;
			const reviewChallenge =
				typeof diagnostics.assignment?.reviewChallenge === 'string'
					? diagnostics.assignment.reviewChallenge.trim()
					: '';
			return {
				rowId: String(rowId),
				assignedTechId: toSnapshotId(resolved?.assignedTechId),
				entityId: toSnapshotId(resolved?.entityId),
				supervisorId: toSnapshotId(resolved?.supervisorId),
				workCode: toSnapshotWorkCode(resolved?.workCode),
				...(reviewChallenge ? { reviewChallenge } : {}),
			};
		})
		.filter(Boolean)
		.sort((left, right) => left.rowId.localeCompare(right.rowId));

export const getAssignmentSemanticSnapshot = (orders, assignedTechIdField) =>
	getAssignmentReviewSnapshot(orders, assignedTechIdField).map(
		({ reviewChallenge, ...assignment }) => assignment,
	);

export const getAssignmentSnapshotFingerprint = (orders, assignedTechIdField) =>
	JSON.stringify(getAssignmentSemanticSnapshot(orders, assignedTechIdField));

export const getAssignmentReviewProofFingerprint = (
	orders,
	assignedTechIdField,
) =>
	JSON.stringify(
		asArray(orders)
			.map(order => {
				const rowId = getImportRowIdentity(order);
				if (!hasValue(rowId)) return;
				const assignment = getImportDiagnostics(order).assignment;
				return {
					rowId: String(rowId),
					reviewChallenge: hasValue(assignment?.reviewChallenge)
						? String(assignment.reviewChallenge)
						: null,
					approvalRequired: assignment?.approvalRequired === true,
				};
			})
			.filter(Boolean)
			.sort((left, right) => left.rowId.localeCompare(right.rowId)),
	);

const getSemanticAssignmentFromSnapshotItem = value => ({
	assignedTechId: toSnapshotId(value?.assignedTechId),
	entityId: toSnapshotId(value?.entityId),
	supervisorId: toSnapshotId(value?.supervisorId),
	workCode: toSnapshotWorkCode(value?.workCode),
});

const isSameSemanticAssignment = (left, right) =>
	JSON.stringify(getSemanticAssignmentFromSnapshotItem(left)) ===
	JSON.stringify(getSemanticAssignmentFromSnapshotItem(right));

export const applyAssignmentReviewSnapshot = (orders, snapshot) => {
	const proofByRowId = new Map();
	const duplicateRowIds = new Set();
	asArray(snapshot).forEach(item => {
		if (!hasValue(item?.rowId)) return;
		const rowId = String(item.rowId);
		if (proofByRowId.has(rowId)) duplicateRowIds.add(rowId);
		proofByRowId.set(rowId, item);
	});
	return asArray(orders).map(order => {
		const rowId = getImportRowIdentity(order);
		const proof = hasValue(rowId) ? proofByRowId.get(String(rowId)) : undefined;
		if (!proof || duplicateRowIds.has(String(rowId))) return order;
		const diagnostics = getImportDiagnostics(order);
		const assignment = asObject(diagnostics.assignment);
		const resolved = asObject(assignment.resolved);
		const reviewChallenge = hasValue(proof.reviewChallenge)
			? String(proof.reviewChallenge)
			: null;
		return {
			...order,
			importDiagnostics: {
				...diagnostics,
				assignment: {
					...assignment,
					resolved: {
						...resolved,
						...getSemanticAssignmentFromSnapshotItem(proof),
					},
					reviewChallenge,
					approvalRequired:
						typeof assignment.approvalRequired === 'boolean'
							? assignment.approvalRequired
							: reviewChallenge !== null,
				},
			},
		};
	});
};

export const getAssignmentReviewErrorState = (
	response,
	reviewedOrders = [],
	assignedTechIdField,
) => {
	const envelope = asObject(response);
	if (
		envelope.code === 'IMPORT_REVIEW_STALE' ||
		envelope.name === 'IMPORT_REVIEW_STALE'
	)
		return;
	if (
		![
			'ORDER_ASSIGNMENT_CHANGED_SINCE_REVIEW',
			'ORDER_ASSIGNMENT_REVIEW_REQUIRED',
		].includes(envelope.code) &&
		![
			'ORDER_ASSIGNMENT_CHANGED_SINCE_REVIEW',
			'ORDER_ASSIGNMENT_REVIEW_REQUIRED',
		].includes(envelope.name)
	)
		return;

	const changes = asArray(envelope.changes);
	const challengeInvalid = changes.some(
		change => change?.challengeInvalid === true,
	);
	const semanticChangeRowIds = Array.from(
		new Set(
			changes
				.filter(
					change =>
						change?.challengeInvalid !== true &&
						change?.duplicate !== true &&
						!isSameSemanticAssignment(change?.reviewed, change?.resolved),
				)
				.map(change => change?.rowId)
				.filter(hasValue)
				.map(String),
		),
	);
	const responseOrders = asArray(envelope.orders);
	const localDriftRowIds = responseOrders.length
		? getAssignmentDriftRowIds(
				reviewedOrders,
				responseOrders,
				assignedTechIdField,
			)
		: [];
	const actualDriftRowIds = Array.from(
		new Set([...semanticChangeRowIds, ...localDriftRowIds]),
	);

	if (envelope.reason === 'assignment_changed')
		return { kind: 'assignment_changed', rowIds: actualDriftRowIds };
	if (envelope.reason === 'challenge_invalid')
		return { kind: 'challenge_invalid', rowIds: [] };
	if (envelope.reason === 'approval_required')
		return { kind: 'approval_required', rowIds: [] };

	// Legacy envelopes did not expose `reason`; infer only from trustworthy
	// evidence and never turn an empty/generic review into technician drift.
	if (challengeInvalid) return { kind: 'challenge_invalid', rowIds: [] };
	if (semanticChangeRowIds.length || localDriftRowIds.length)
		return { kind: 'assignment_changed', rowIds: actualDriftRowIds };
	if (changes.length === 0) return { kind: 'approval_required', rowIds: [] };
	return { kind: 'challenge_invalid', rowIds: [] };
};

export const getAssignmentDriftRowIds = (
	reviewedOrders,
	refreshedOrders,
	assignedTechIdField,
) => {
	const reviewed = new Map(
		getAssignmentSemanticSnapshot(reviewedOrders, assignedTechIdField).map(
			item => [item.rowId, item],
		),
	);
	return Array.from(
		new Set(
			getAssignmentSemanticSnapshot(refreshedOrders, assignedTechIdField)
				.filter(
					item =>
						JSON.stringify(reviewed.get(item.rowId)) !== JSON.stringify(item),
				)
				.map(item => item.rowId),
		),
	);
};

export const markAssignmentDrift = (
	refreshedOrders,
	reviewedOrders,
	assignedTechIdField,
) => {
	const reviewedByRow = new Map(
		asArray(reviewedOrders).map(order => [getImportRowIdentity(order), order]),
	);
	const driftIds = new Set(
		getAssignmentDriftRowIds(
			reviewedOrders,
			refreshedOrders,
			assignedTechIdField,
		),
	);
	return asArray(refreshedOrders).map(order => {
		const rowId = getImportRowIdentity(order);
		if (!driftIds.has(String(rowId))) return order;
		const diagnostics = getImportDiagnostics(order);
		const assignment = asObject(diagnostics.assignment);
		const previousResolved = getAssignmentPresentation(
			reviewedByRow.get(rowId),
			assignedTechIdField,
		).resolved;
		const issues = asArray(diagnostics.issues).filter(
			issue => issue?.code !== 'ASSIGNMENT_CHANGED_SINCE_REVIEW',
		);
		issues.push({
			code: 'ASSIGNMENT_CHANGED_SINCE_REVIEW',
			severity: 'warning',
			blocking: false,
		});
		return {
			...order,
			importDiagnostics: {
				...diagnostics,
				issues,
				assignment: { ...assignment, previousResolved },
			},
		};
	});
};

export const removeImportRows = (orders, rowIdentities) => {
	const identities = new Set(
		asArray(rowIdentities).filter(
			identity => identity !== undefined && identity !== null,
		),
	);
	return asArray(orders).filter(order => {
		const identity = getImportRowIdentity(order);
		return !identities.has(identity !== undefined ? identity : order?.id);
	});
};

export const getZoneCoverageItems = order =>
	asArray(getImportDiagnostics(order).zoneCoverage?.items).filter(
		item => item && typeof item === 'object' && !Array.isArray(item),
	);

export const getZoneCoverageActions = item =>
	Array.from(
		new Set(
			asArray(item?.actions).filter(action =>
				ZONE_COVERAGE_ACTIONS.has(action),
			),
		),
	).sort();

export const getZoneCoverageChangeItems = orders => {
	const relationships = new Map();
	asArray(orders).forEach(order => {
		getZoneCoverageItems(order)
			.filter(
				item =>
					item.status === 'will_provision' &&
					getZoneCoverageActions(item).length > 0,
			)
			.forEach(item => {
				// field intentionally is not part of the key: contract and order zones can
				// authorize the exact same technician coverage provisioning plan.
				const actions = getZoneCoverageActions(item);
				const key = [
					item.assignedTechId,
					item.zoneId || item.pendingZoneKey || item.planId,
					item.serviceId,
					actions.join(','),
					item.restoredDepartmentWideCoverage === true
						? 'department-wide'
						: 'zone-scoped',
				].join(':');
				if (!relationships.has(key))
					relationships.set(key, { ...item, actions });
			});
	});
	return Array.from(relationships.values());
};

export const getZoneCoverageChangeKeys = orders =>
	getZoneCoverageChangeItems(orders).map(item =>
		[
			item.assignedTechId,
			item.zoneId || item.pendingZoneKey || item.planId,
			item.serviceId,
			asArray(item.actions).join(','),
			item.restoredDepartmentWideCoverage === true
				? 'department-wide'
				: 'zone-scoped',
		].join(':'),
	);

export const getZoneCoverageChangeCount = orders =>
	getZoneCoverageChangeItems(orders).length;

export const getIssueMessage = (issue, order) => {
	const value = asObject(issue);
	if (
		order &&
		[
			'CONTRACT_FREQUENCY_REVIEW_REQUIRED',
			'CONTRACT_FREQUENCY_UNKNOWN',
		].includes(value.code)
	)
		return getContractFrequencyReviewMessage(order);
	if (value.code === 'REQUIRED_FIELD_MISSING') {
		const fieldLabel =
			REQUIRED_FIELD_LABELS[value.field] ||
			value.fieldName ||
			FIELD_LABELS[value.field];
		return fieldLabel
			? `Falta completar ${fieldLabel}.`
			: 'Falta completar un campo requerido.';
	}
	if (ISSUE_MESSAGES[value.code]) return ISSUE_MESSAGES[value.code];
	if (value.code === 'LEGACY_FIELD_VALIDATION' && value.message)
		return value.message;
	if (value.message && value.message !== value.code) return value.message;
	return 'Este campo requiere revisión.';
};

export const getReasonMessage = reason =>
	REASON_MESSAGES[reason] || 'La coincidencia requiere revisión adicional.';

export const getOrderIssues = (order, fields = [], locationFields = {}) => {
	const diagnostics = getImportDiagnostics(order);
	const issues = Array.isArray(diagnostics.issues)
		? diagnostics.issues.slice()
		: asArray(fields)
				.filter(field => order?.[`${field.id}Validation`] === 'unsuccess')
				.map(field => ({
					code: 'LEGACY_FIELD_VALIDATION',
					field: field.id,
					severity: 'error',
					blocking: true,
					message: `${field.name || field.id} requiere corrección.`,
				}));

	if (
		!Array.isArray(diagnostics.issues) &&
		!issues.length &&
		order?.mappingErrors === 'N/A'
	) {
		issues.push({
			code: 'LEGACY_MAPPING_ERROR',
			severity: 'error',
			blocking: true,
			message: ISSUE_MESSAGES.LEGACY_MAPPING_ERROR,
		});
	}

	asArray(diagnostics.assignment?.issues).forEach(issue => {
		if (
			issue &&
			!issues.some(
				existing =>
					existing?.code === issue?.code && existing?.field === issue?.field,
			)
		)
			issues.push(issue);
	});

	issues.forEach((issue, index) => {
		if (!String(issue?.code || '').startsWith('ZONE_COVERAGE_')) return;
		issues[index] = {
			...issue,
			severity: 'warning',
			blocking: false,
		};
	});

	getZoneCoverageItems(order).forEach(item => {
		if (!['will_provision', 'warning_only'].includes(item.status)) return;
		const existingIssueIndex = issues.findIndex(
			issue =>
				issue?.code === item.code &&
				(!issue?.field || issue?.field === item.field),
		);
		if (existingIssueIndex >= 0) {
			issues[existingIssueIndex] = {
				...issues[existingIssueIndex],
				severity: 'warning',
				blocking: false,
				zoneCoverageItem: item,
			};
			return;
		}
		issues.push({
			code: item.code,
			field: item.field,
			severity: 'warning',
			blocking: false,
			zoneCoverageItem: item,
		});
	});

	const normalizedLocationFields = getLocationFields(fields, locationFields);
	const matches = getEffectiveLocationMatches(diagnostics);
	Object.keys(matches).forEach(fieldId => {
		if (
			matches[fieldId]?.strategy === 'parent_fallback' &&
			!issues.some(
				issue =>
					issue?.code === 'LOCATION_MISSING_ZONE_FALLBACK' &&
					issue?.field === fieldId,
			)
		) {
			issues.push({
				code: 'LOCATION_MISSING_ZONE_FALLBACK',
				field: fieldId,
				severity: 'warning',
				blocking: false,
			});
		}
	});

	getApplicableZoneFields(order, normalizedLocationFields.zoneFields).forEach(
		zoneIdField => {
			const assigned = getAssignedLocation({
				order,
				...normalizedLocationFields,
				zoneIdField,
			});
			const missingLevels = ['city', 'township', 'zone'].filter(
				level => !hasValue(assigned[level]),
			);
			if (
				missingLevels.length &&
				!issues.some(
					issue => issue?.blocking === true && issue?.field === zoneIdField.id,
				)
			) {
				issues.push({
					code: 'LOCATION_ASSIGNED_PATH_INCOMPLETE',
					field: zoneIdField.id,
					severity: 'error',
					blocking: true,
					missingLevels,
				});
			}
		},
	);

	return issues;
};

export const getFieldIssues = (
	order,
	fields = [],
	fieldId,
	locationFields = {},
) =>
	getOrderIssues(order, fields, locationFields).filter(
		issue => issue?.field === fieldId,
	);

export const isImportableMainEntityAssignmentFallback = order => {
	const diagnostics = getImportDiagnostics(order);
	const assignment = asObject(diagnostics.assignment);
	return isImportableMainEntityFallback(assignment, diagnostics.issues);
};

const classifyOrderReviewStatus = ({
	order,
	issues,
	contractFrequencyCreationResolved = false,
}) => {
	const diagnostics = getImportDiagnostics(order);
	if (issues.some(issue => issue?.blocking === true))
		return REVIEW_STATUS.BLOCKED;
	if (isImportableMainEntityAssignmentFallback(order))
		return REVIEW_STATUS.AUTO_CORRECTED;
	const frequency = getContractFrequencyPresentation(order);
	if (frequency.status === 'unknown' && contractFrequencyCreationResolved)
		return REVIEW_STATUS.AUTO_CORRECTED;
	if (
		['unknown', 'ambiguous', 'inactive', 'contract_conflict'].includes(
			frequency.status,
		)
	)
		return REVIEW_STATUS.BLOCKED;
	if (frequency.status === 'review_required')
		return REVIEW_STATUS.REVIEW_REQUIRED;
	if (
		frequency.status === 'blank_preserved' ||
		(frequency.status === 'resolved' && frequency.strategy === 'manual')
	)
		return REVIEW_STATUS.AUTO_CORRECTED;
	const service = getServicePresentation(order);
	if (
		service.assigned &&
		['normalized', 'fuzzy', 'manual', 'create_new'].includes(service.strategy)
	)
		return service.needsReview
			? REVIEW_STATUS.REVIEW_REQUIRED
			: REVIEW_STATUS.AUTO_CORRECTED;
	if (
		service.needsReview &&
		['ambiguous', 'unresolved'].includes(service.strategy)
	)
		return REVIEW_STATUS.REVIEW_REQUIRED;
	if (asArray(diagnostics.dateDefaults || diagnostics.date_defaults).length > 0)
		return REVIEW_STATUS.AUTO_CORRECTED;
	const matches = getEffectiveLocationMatches(diagnostics);
	if (
		Object.keys(matches).some(
			fieldId =>
				matches[fieldId]?.strategy === 'parent_fallback' ||
				matches[fieldId]?.strategy === 'missing_city_fallback',
		)
	)
		return REVIEW_STATUS.AUTO_CORRECTED;

	const normalizedStatus =
		STATUS_ALIASES[String(diagnostics.status || '').toLowerCase()];
	if (normalizedStatus === REVIEW_STATUS.BLOCKED)
		return issues.length ? REVIEW_STATUS.REVIEW_REQUIRED : REVIEW_STATUS.READY;
	if (normalizedStatus) return normalizedStatus;
	if (issues.length) return REVIEW_STATUS.REVIEW_REQUIRED;

	return REVIEW_STATUS.READY;
};

export const getEffectiveOrderReviewState = ({
	order,
	fields = [],
	locationFields = {},
	contractFrequencyCreationSelections = [],
}) => {
	let contractFrequencyCreationResolved = false;
	const issues = getOrderIssues(order, fields, locationFields).filter(issue => {
		const resolved = isContractFrequencyCreationIssueResolved({
			order,
			issue,
			selections: contractFrequencyCreationSelections,
		});
		if (resolved) contractFrequencyCreationResolved = true;
		return !resolved;
	});
	return {
		issues,
		status: classifyOrderReviewStatus({
			order,
			issues,
			contractFrequencyCreationResolved,
		}),
	};
};

export const getOrderReviewStatus = (
	order,
	fields = [],
	locationFields = {},
	reviewContext = {},
) =>
	getEffectiveOrderReviewState({
		order,
		fields,
		locationFields,
		contractFrequencyCreationSelections:
			asObject(reviewContext).contractFrequencyCreationSelections,
	}).status;

export const getReviewSummary = (
	orders,
	fields = [],
	locationFields = {},
	reviewContext = {},
) => {
	const summary = {
		[REVIEW_FILTER.ALL]: 0,
		[REVIEW_STATUS.READY]: 0,
		[REVIEW_STATUS.AUTO_CORRECTED]: 0,
		[REVIEW_STATUS.REVIEW_REQUIRED]: 0,
		[REVIEW_STATUS.BLOCKED]: 0,
	};

	asArray(orders).forEach(order => {
		const status = getOrderReviewStatus(
			order,
			fields,
			locationFields,
			reviewContext,
		);
		summary[REVIEW_FILTER.ALL] += 1;
		summary[status] += 1;
	});

	return summary;
};

export const hasBlockingIssues = (
	orders,
	fields = [],
	locationFields = {},
	reviewContext = {},
) =>
	asArray(orders).some(order =>
		getEffectiveOrderReviewState({
			order,
			fields,
			locationFields,
			contractFrequencyCreationSelections:
				asObject(reviewContext).contractFrequencyCreationSelections,
		}).issues.some(issue => issue?.blocking === true),
	);

export const requiresRevalidation = orders =>
	asArray(orders).some(
		order => getImportDiagnostics(order).needsRevalidation === true,
	);

export const markOrderForRevalidation = order => ({
	...order,
	importDiagnostics: {
		...getImportDiagnostics(order),
		needsRevalidation: true,
	},
});

export const applyManualLocationSelection = ({
	order,
	option,
	fieldId,
	zoneIdField,
	cityIdField,
	townshipIdField,
}) => {
	const diagnostics = getImportDiagnostics(order);
	const currentMatches = asObject(diagnostics.locationMatches);
	const previousMatch =
		currentMatches[fieldId] || diagnostics.locationMatch || {};
	const previousMatchWithoutCreation = { ...asObject(previousMatch) };
	delete previousMatchWithoutCreation.zoneCreation;
	const updatedOrder = markOrderForRevalidation({
		...order,
		[zoneIdField.id]: option.zoneId,
		[zoneIdField.name]: option.zoneName,
		[townshipIdField.id]: option.townshipId,
		[townshipIdField.name]: option.townshipName,
		[cityIdField.id]: option.cityId,
		[cityIdField.name]: option.cityName,
	});
	delete updatedOrder[String(zoneIdField.id).replace('Id', '')];
	updatedOrder.importDiagnostics = {
		...diagnostics,
		needsRevalidation: true,
		// Preserve all issues until creationAssignment returns authoritative
		// diagnostics. Never clear the issue for the other location field.
		locationMatches: {
			...currentMatches,
			[fieldId]: {
				...previousMatchWithoutCreation,
				strategy: 'manual_pending_validation',
				confidence: undefined,
				assigned: {
					cityId: option.cityId,
					cityName: option.cityName,
					townshipId: option.townshipId,
					townshipName: option.townshipName,
					zoneId: option.zoneId,
					zoneName: option.zoneName,
				},
			},
		},
	};
	return updatedOrder;
};

export const getSourceValues = (order, fieldId) => {
	const values = asObject(getImportDiagnostics(order).sourceValues);
	if (!fieldId) return values;
	const zoneKey = getZoneSourceKey(fieldId);
	return {
		city: values.city,
		township: values.township,
		zone:
			values[zoneKey] !== undefined && values[zoneKey] !== null
				? values[zoneKey]
				: values.zone,
	};
};

export const getSourceProvenance = (order, fieldId) => {
	const provenance = asObject(getImportDiagnostics(order).sourceProvenance);
	if (!fieldId) return provenance;
	const zoneKey = getZoneSourceKey(fieldId);
	return asObject(provenance[zoneKey] || provenance.zone);
};

export const getLocationMatch = (order, fieldId) => {
	const diagnostics = getImportDiagnostics(order);
	const locationMatches = getEffectiveLocationMatches(diagnostics);
	if (fieldId) return asObject(locationMatches[fieldId]);
	const matchFields = Object.keys(locationMatches);
	return matchFields.length === 1
		? asObject(locationMatches[matchFields[0]])
		: {};
};

const getZoneCreationResults = order => {
	const direct = asObject(order?.zoneCreationResult);
	return [
		...(Object.keys(direct).length ? [direct] : []),
		...asArray(order?.zoneCreationResults),
	].filter(
		result =>
			result &&
			['created', 'reused'].includes(String(result.status || '').toLowerCase()),
	);
};

export const getZoneCreationResult = (order, fieldId) =>
	getZoneCreationResults(order).find(
		result => !fieldId || String(result.fieldId) === String(fieldId),
	);

const ZONE_CREATION_LEVEL_ORDER = ['city', 'township', 'zone'];
const ZONE_CREATION_PLAN_ACTIONS = ['create', 'reuse'];

const normalizeZoneCreationLevels = levels => {
	const unique = new Map();
	asArray(levels).forEach(value => {
		const level = asObject(value);
		const type = String(level.type || '').toLowerCase();
		const action = String(level.action || '').toLowerCase();
		const name = String(level.name || '').trim();
		if (
			!ZONE_CREATION_LEVEL_ORDER.includes(type) ||
			!ZONE_CREATION_PLAN_ACTIONS.includes(action) ||
			!name ||
			unique.has(type)
		)
			return;
		const numericId = Number(level.id);
		unique.set(type, {
			type,
			action,
			id:
				level.id !== null &&
				level.id !== undefined &&
				Number.isFinite(numericId)
					? numericId
					: null,
			name,
			...(type === 'zone' && hasValue(level.distance)
				? { distance: String(level.distance) }
				: {}),
		});
	});
	return ZONE_CREATION_LEVEL_ORDER.map(type => unique.get(type)).filter(
		Boolean,
	);
};

const normalizeZoneCreation = value => {
	const creation = asObject(value);
	if (
		creation.eligible !== true ||
		!['available', 'selected'].includes(creation.status) ||
		!hasValue(creation.fieldId) ||
		!hasValue(creation.eligibilityChallenge)
	)
		return;
	const source = asObject(creation.source);
	const location = asObject(creation.location);
	return {
		...creation,
		challenge: String(creation.eligibilityChallenge),
		fieldId: String(creation.fieldId),
		sourceKey: hasValue(creation.sourceKey)
			? String(creation.sourceKey)
			: undefined,
		cohortKey: hasValue(creation.cohortKey)
			? String(creation.cohortKey)
			: undefined,
		catalogKey: hasValue(creation.catalogKey)
			? String(creation.catalogKey)
			: undefined,
		pendingZoneKey: hasValue(creation.pendingZoneKey)
			? String(creation.pendingZoneKey)
			: undefined,
		source: {
			city: source.city,
			township: source.township,
			zone: source.zone,
		},
		location: {
			cityId: location.cityId,
			cityName: location.cityName,
			townshipId: location.townshipId,
			townshipName: location.townshipName,
			zoneId: null,
			zoneName: location.zoneName,
			distance: location.distance,
		},
		levels: normalizeZoneCreationLevels(creation.levels),
		targetRowIds: asArray(creation.targetRowIds).filter(hasValue).map(String),
		affectedCount: Number.isFinite(Number(creation.affectedCount))
			? Number(creation.affectedCount)
			: asArray(creation.targetRowIds).length,
	};
};

export const getZoneCreationOption = (order, fieldId) => {
	if (isExistingImportOrder(order)) return;
	if (getZoneCreationResult(order, fieldId)) return;
	const creation = normalizeZoneCreation(
		getLocationMatch(order, fieldId).zoneCreation,
	);
	return creation && creation.fieldId === fieldId ? creation : undefined;
};

export const isOrderTargetedByZoneCreation = (order, fieldId, selection) => {
	if (isExistingImportOrder(order)) return false;
	if (!selection || String(selection.fieldId) !== String(fieldId)) return false;
	const rowId = getImportRowIdentity(order);
	return (
		hasValue(rowId) &&
		asArray(selection.targetRowIds).map(String).includes(String(rowId))
	);
};

export const isZoneCreationSelectionAllowed = (orders, selection) => {
	if (!selection || !Array.isArray(selection.targetRowIds)) return false;
	const targetRowIds = new Set(
		selection.targetRowIds.filter(hasValue).map(String),
	);
	if (!targetRowIds.size) return false;
	return !asArray(orders).some(
		order =>
			isExistingImportOrder(order) &&
			targetRowIds.has(String(getImportRowIdentity(order))),
	);
};

const getSelectedZoneCreations = orders => {
	const plans = new Map();
	asArray(orders).forEach(order => {
		if (isExistingImportOrder(order)) return;
		const completedPendingKeys = new Set(
			getZoneCreationResults(order)
				.map(result => result.pendingZoneKey)
				.filter(hasValue)
				.map(String),
		);
		const matches = getEffectiveLocationMatches(getImportDiagnostics(order));
		Object.keys(matches).forEach(fieldId => {
			const creation = normalizeZoneCreation(matches[fieldId]?.zoneCreation);
			if (!creation || creation.status !== 'selected') return;
			if (!isZoneCreationSelectionAllowed(orders, creation)) return;
			if (
				creation.pendingZoneKey &&
				completedPendingKeys.has(String(creation.pendingZoneKey))
			)
				return;
			const key =
				creation.pendingZoneKey ||
				creation.cohortKey ||
				`${creation.fieldId}:${creation.sourceKey || creation.challenge}`;
			if (!plans.has(key)) plans.set(key, creation);
		});
	});
	return Array.from(plans.values());
};

export const getZoneCreationSelectionsFromOrders = orders =>
	getSelectedZoneCreations(orders);

export const clearZoneCreationSelection = ({ order, selection }) => {
	if (
		!selection ||
		!isOrderTargetedByZoneCreation(order, selection.fieldId, selection)
	)
		return order;
	const diagnostics = getImportDiagnostics(order);
	const matches = getEffectiveLocationMatches(diagnostics);
	const currentMatch = asObject(matches[selection.fieldId]);
	const creation = normalizeZoneCreation(currentMatch.zoneCreation);
	if (!creation || creation.challenge !== selection.challenge) return order;
	if (creation.status === 'available') return order;
	const previous = asObject(creation.previous);
	const restoredBase = Object.keys(previous).length
		? previous
		: { ...currentMatch, zoneCreation: undefined };
	delete restoredBase.zoneCreation;
	const restoredMatch = {
		...restoredBase,
		zoneCreation: {
			...asObject(currentMatch.zoneCreation),
			status: 'available',
		},
	};
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			locationMatches: {
				...matches,
				[selection.fieldId]: restoredMatch,
			},
		},
	};
};

export const getZoneCreationReviewSnapshot = orders =>
	getSelectedZoneCreations(orders)
		.map(plan => ({
			challenge: plan.challenge,
			planFingerprint: hasValue(plan.planFingerprint)
				? String(plan.planFingerprint)
				: null,
		}))
		.sort((left, right) => left.challenge.localeCompare(right.challenge));

export const getZoneCreationSnapshotFingerprint = orders =>
	JSON.stringify(getZoneCreationReviewSnapshot(orders));

export const getZoneCreationSelectionsPayload = (selections, orders) =>
	Array.from(
		new Set(
			asArray(selections)
				.filter(selection =>
					Array.isArray(orders)
						? isZoneCreationSelectionAllowed(orders, selection)
						: true,
				)
				.map(selection => selection?.challenge)
				.filter(hasValue)
				.map(String),
		),
	)
		.sort()
		.map(challenge => ({ challenge }));

export const getZoneCreationSummary = (orders, localSelections = []) => {
	const plans = [
		...getSelectedZoneCreations(orders),
		...asArray(localSelections).filter(selection =>
			isZoneCreationSelectionAllowed(orders, selection),
		),
	];
	const uniquePlans = new Map();
	plans.forEach(plan => {
		if (!plan) return;
		const key =
			plan.pendingZoneKey ||
			plan.cohortKey ||
			`${plan.fieldId || ''}:${plan.sourceKey || plan.challenge || ''}`;
		if (!uniquePlans.has(key)) uniquePlans.set(key, plan);
	});
	const affectedRows = new Set();
	let rowsWithoutIdentity = 0;
	const physicalZones = new Set();
	uniquePlans.forEach(plan => {
		physicalZones.add(
			plan.catalogKey
				? `catalog:${plan.catalogKey}`
				: `plan:${
						plan.pendingZoneKey ||
						plan.cohortKey ||
						`${plan.fieldId || ''}:${plan.sourceKey || plan.challenge || ''}`
					}`,
		);
		const targets = asArray(plan.targetRowIds).filter(hasValue).map(String);
		if (targets.length) targets.forEach(rowId => affectedRows.add(rowId));
		else rowsWithoutIdentity += Math.max(0, Number(plan.affectedCount) || 0);
	});
	return {
		zoneCount: physicalZones.size,
		affectedOrderCount: affectedRows.size + rowsWithoutIdentity,
	};
};

export const getDisplayConfidence = match =>
	asObject(match).strategy === 'parent_fallback'
		? undefined
		: asObject(match).confidence;

const getLocationFromOption = (option, fallback = {}) => ({
	city: option.cityName || option.city || fallback.city || '',
	township: option.townshipName || option.township || fallback.township || '',
	zone: option.zoneName || option.zone || fallback.zone || '',
});

export const isCanonicalLocationOption = option =>
	[
		'cityId',
		'townshipId',
		'zoneId',
		'cityName',
		'townshipName',
		'zoneName',
	].every(key => hasValue(option?.[key]));

const getOrderLocation = ({
	order,
	cityIdField,
	townshipIdField,
	zoneIdField,
}) => ({
	city:
		(cityIdField && (order?.[cityIdField.name] || order?.[cityIdField.id])) ||
		'',
	township:
		(townshipIdField &&
			(order?.[townshipIdField.name] || order?.[townshipIdField.id])) ||
		'',
	zone:
		(zoneIdField && (order?.[zoneIdField.name] || order?.[zoneIdField.id])) ||
		'',
});

export const formatLocation = (
	location,
	{
		missingCityLabel = 'Ciudad no informada',
		missingTownshipLabel = 'Cantón no informado',
		missingZoneLabel = 'Zona no informada',
	} = {},
) =>
	[
		hasValue(location?.city) ? location.city : missingCityLabel,
		hasValue(location?.township) ? location.township : missingTownshipLabel,
		hasValue(location?.zone) ? location.zone : missingZoneLabel,
	].join(' › ');

export const hasLocationSourceValue = location =>
	['city', 'township', 'zone'].some(key => hasValue(location?.[key]));

export const getOrderIdentifier = (order, odtIdField) =>
	(odtIdField && (order?.[odtIdField.id] || order?.[odtIdField.name])) ||
	order?.odtId ||
	order?.order_odt_id ||
	order?.id ||
	'Sin identificador';

export const getCandidateOption = candidate => {
	const value = asObject(candidate);
	const cityName = value.cityName || value.city_name || value.city;
	const townshipName =
		value.townshipName || value.township_name || value.township;
	const zoneName =
		value.zoneName || value.zone_name || value.name || value.zone;
	return {
		cityId: value.cityId || value.city_id,
		cityName,
		city: cityName,
		townshipId: value.townshipId || value.township_id,
		townshipName,
		township: townshipName,
		zoneId: value.zoneId || value.zone_id || value.id,
		zoneName,
		zone: zoneName,
		confidence:
			value.confidence !== undefined && value.confidence !== null
				? value.confidence
				: value.score,
	};
};

export const getOrderCandidates = (order, fieldId) =>
	asArray(getLocationMatch(order, fieldId).candidates).map(getCandidateOption);

export const getLocationPresentation = ({
	order,
	cityIdField,
	townshipIdField,
	zoneIdField,
}) => {
	const match = getLocationMatch(order, zoneIdField?.id);
	const assignedOption = getCandidateOption(match.assigned);
	const orderLocation = getOrderLocation({
		order,
		cityIdField,
		townshipIdField,
		zoneIdField,
	});
	const hasAuthoritativeMatch = Object.keys(match).length > 0;
	const hasCanonicalAssignment = isCanonicalLocationOption(assignedOption);
	const zoneCreation = getZoneCreationOption(order, zoneIdField?.id);
	const zoneCreationResult = getZoneCreationResult(order, zoneIdField?.id);

	if (zoneCreationResult) {
		return {
			state: LOCATION_PRESENTATION_STATE.ASSIGNED,
			label: 'Creada y asignada por Synapse',
			location: {
				city: zoneCreationResult.cityName,
				township: zoneCreationResult.townshipName,
				zone: zoneCreationResult.zoneName,
			},
			option: getCandidateOption(zoneCreationResult),
			match,
			zoneCreationResult,
		};
	}

	if (zoneCreation?.status === 'selected') {
		const pendingLocation = {
			city:
				zoneCreation.location.cityName ||
				zoneCreation.source.city ||
				orderLocation.city,
			township:
				zoneCreation.location.townshipName ||
				zoneCreation.source.township ||
				orderLocation.township,
			zone:
				zoneCreation.location.zoneName ||
				zoneCreation.source.zone ||
				orderLocation.zone,
		};
		return {
			state: LOCATION_PRESENTATION_STATE.PENDING_CREATION,
			label:
				LOCATION_PRESENTATION_LABEL[
					LOCATION_PRESENTATION_STATE.PENDING_CREATION
				],
			location: pendingLocation,
			option: assignedOption,
			match,
			zoneCreation,
		};
	}

	if (hasCanonicalAssignment) {
		const state =
			match.strategy === 'manual_pending_validation'
				? LOCATION_PRESENTATION_STATE.PENDING_VALIDATION
				: LOCATION_PRESENTATION_STATE.ASSIGNED;
		return {
			state,
			label: LOCATION_PRESENTATION_LABEL[state],
			location: getLocationFromOption(assignedOption, orderLocation),
			option: assignedOption,
			match,
		};
	}

	const suggestedOption = getOrderCandidates(order, zoneIdField?.id)[0];
	if (isCanonicalLocationOption(suggestedOption)) {
		return {
			state: LOCATION_PRESENTATION_STATE.SUGGESTED,
			label: LOCATION_PRESENTATION_LABEL[LOCATION_PRESENTATION_STATE.SUGGESTED],
			location: getLocationFromOption(suggestedOption),
			option: suggestedOption,
			match,
		};
	}

	// Compatibility is limited to legacy payloads that have no resolver match and
	// carry an actual zone ID. Raw display names alone never prove assignment.
	if (
		!hasAuthoritativeMatch &&
		hasValue(zoneIdField && order?.[zoneIdField.id])
	) {
		return {
			state: LOCATION_PRESENTATION_STATE.ASSIGNED,
			label: LOCATION_PRESENTATION_LABEL[LOCATION_PRESENTATION_STATE.ASSIGNED],
			location: orderLocation,
			option: getCandidateOption({
				city: orderLocation.city,
				township: orderLocation.township,
				zoneId: order?.[zoneIdField.id],
				zone: orderLocation.zone,
			}),
			match,
		};
	}

	return {
		state: LOCATION_PRESENTATION_STATE.UNASSIGNED,
		label: LOCATION_PRESENTATION_LABEL[LOCATION_PRESENTATION_STATE.UNASSIGNED],
		location: { city: '', township: '', zone: '' },
		option: undefined,
		match,
	};
};

export const getAssignedLocation = args => {
	const presentation = getLocationPresentation(args);
	return presentation.state === LOCATION_PRESENTATION_STATE.ASSIGNED ||
		presentation.state === LOCATION_PRESENTATION_STATE.PENDING_VALIDATION ||
		presentation.state === LOCATION_PRESENTATION_STATE.PENDING_CREATION
		? presentation.location
		: { city: '', township: '', zone: '' };
};

export const filterReviewOrders = ({
	orders,
	fields = [],
	filter = REVIEW_FILTER.ALL,
	search = '',
	odtIdField,
	locationFields = {},
	contractFrequencyCreationSelections = [],
}) => {
	const normalizedSearch = String(search || '')
		.trim()
		.toLowerCase();

	return asArray(orders).filter(order => {
		const effectiveReview = getEffectiveOrderReviewState({
			order,
			fields,
			locationFields,
			contractFrequencyCreationSelections,
		});
		const matchesFilter =
			filter === REVIEW_FILTER.ALL || effectiveReview.status === filter;
		if (!matchesFilter) return false;
		if (!normalizedSearch) return true;

		const zoneFields = getZoneFields(locationFields);
		const comparisons = zoneFields.length
			? zoneFields.reduce((values, zoneIdField) => {
					values.push(formatLocation(getSourceValues(order, zoneIdField.id)));
					values.push(
						formatLocation(
							getLocationPresentation({
								order,
								...locationFields,
								zoneIdField,
							}).location,
						),
					);
					return values;
				}, [])
			: [
					formatLocation(getSourceValues(order)),
					formatLocation(
						getLocationPresentation({ order, ...locationFields }).location,
					),
				];
		const issues = effectiveReview.issues;
		const service = getServicePresentation(order);
		const frequency = getContractFrequencyPresentation(order);
		const searchableValues = [
			getOrderIdentifier(order, odtIdField),
			...comparisons,
			service.source,
			service.activityType,
			service.assigned?.name,
			...service.candidates.map(candidate => candidate.name),
			frequency.sourceValue,
			frequency.existing?.code,
			frequency.existing?.name,
			frequency.assigned?.code,
			frequency.assigned?.name,
			...frequency.candidates.flatMap(candidate => [
				candidate.code,
				candidate.name,
			]),
			...issues.map(getIssueMessage),
		];

		return searchableValues.some(value =>
			String(value || '')
				.toLowerCase()
				.includes(normalizedSearch),
		);
	});
};

export const findNextProblemOrder = ({
	orders,
	currentOrder,
	fields = [],
	locationFields = {},
}) => {
	const problemOrders = asArray(orders).filter(
		order => getOrderIssues(order, fields, locationFields).length > 0,
	);
	if (!problemOrders.length) return;

	const currentIndex = problemOrders.indexOf(currentOrder);
	return problemOrders[(currentIndex + 1) % problemOrders.length];
};

export const findNextReviewOrder = ({ orders, currentOrder }) => {
	const scopedOrders = asArray(orders);
	if (!scopedOrders.length) return;

	const currentIndex = scopedOrders.indexOf(currentOrder);
	return scopedOrders[(currentIndex + 1) % scopedOrders.length];
};

export const normalizeAssignedOrdersResponse = response => {
	if (Array.isArray(response)) return response;
	return asArray(response?.orders);
};

export const hasSafeImportRowCorrelation = (
	response,
	sourceOrders,
	{ requireExact = false } = {},
) => {
	const responseRows = normalizeAssignedOrdersResponse(response);
	const sources = asArray(sourceOrders);
	const responseIds = responseRows.map(getImportRowIdentity);
	const sourceIds = sources.map(getImportRowIdentity);
	const identifiedCount = responseIds.filter(hasValue).length;
	if (identifiedCount > 0) {
		return (
			identifiedCount === responseRows.length &&
			new Set(responseIds).size === responseIds.length &&
			responseIds.every(rowId => sourceIds.includes(rowId)) &&
			(!requireExact ||
				(responseRows.length === sources.length &&
					sourceIds.every(rowId => responseIds.includes(rowId))))
		);
	}

	const getLegacyKey = order =>
		order?.id ?? order?.odtId ?? order?.order_odt_id;
	const responseKeys = responseRows.map(getLegacyKey);
	const sourceKeys = sources.map(getLegacyKey);
	return (
		responseKeys.every(hasValue) &&
		sourceKeys.every(hasValue) &&
		new Set(responseKeys).size === responseKeys.length &&
		new Set(sourceKeys).size === sourceKeys.length &&
		responseKeys.every(key => sourceKeys.includes(key)) &&
		(!requireExact ||
			(responseRows.length === sources.length &&
				sourceKeys.every(key => responseKeys.includes(key))))
	);
};

export const mergeImportDiagnostics = (
	assignedOrders,
	sourceOrders,
	{ markValidated = false } = {},
) => {
	const sources = asArray(sourceOrders);
	const assigned = normalizeAssignedOrdersResponse(assignedOrders);
	const responseUsesTransportIds =
		assigned.length > 0 &&
		assigned.every(order => hasValue(getImportRowIdentity(order)));
	const sourcesUseTransportIds =
		sources.length > 0 &&
		sources.every(order => hasValue(getImportRowIdentity(order)));
	const consumedSourceIndexes = new Set();
	const getOrderKey = order => order?.id ?? order?.odtId ?? order?.order_odt_id;
	const takeSourceOrder = (order, index) => {
		const responseRowId = getImportRowIdentity(order);
		if (hasValue(responseRowId)) {
			const exactIndex = sources.findIndex(
				(candidate, candidateIndex) =>
					!consumedSourceIndexes.has(candidateIndex) &&
					getImportRowIdentity(candidate) === responseRowId,
			);
			if (exactIndex >= 0) {
				consumedSourceIndexes.add(exactIndex);
				return sources[exactIndex];
			}
			// Never fall back to ODT/index in an identified batch: duplicate ODTs
			// could otherwise inherit each other's technician diagnostics.
			if (responseUsesTransportIds && sourcesUseTransportIds) return;
		}
		const orderKey = getOrderKey(order);
		let sourceIndex =
			orderKey === undefined
				? -1
				: sources.findIndex(
						(candidate, candidateIndex) =>
							!consumedSourceIndexes.has(candidateIndex) &&
							getOrderKey(candidate) === orderKey,
					);
		if (sourceIndex < 0 && !consumedSourceIndexes.has(index))
			sourceIndex = index;
		if (sourceIndex < 0)
			sourceIndex = sources.findIndex(
				(_, candidateIndex) => !consumedSourceIndexes.has(candidateIndex),
			);
		if (sourceIndex < 0) return;
		consumedSourceIndexes.add(sourceIndex);
		return sources[sourceIndex];
	};

	return assigned.map((order, index) => {
		const sourceOrder = takeSourceOrder(order, index);
		const sourceDiagnostics = getImportDiagnostics(sourceOrder);
		const assignedDiagnostics = getImportDiagnostics(order);
		const sourceFrequencyMatch = asObject(sourceDiagnostics.frequencyMatch);
		const assignedFrequencyMatch = asObject(assignedDiagnostics.frequencyMatch);
		const hasAssignedFrequencyMatch = Object.keys(
			assignedFrequencyMatch,
		).length;

		const clientRowId =
			getImportRowIdentity(order) || getImportRowIdentity(sourceOrder);

		if (
			!Object.keys(sourceDiagnostics).length &&
			!Object.keys(assignedDiagnostics).length &&
			!clientRowId
		)
			return order;

		const diagnostics = {
			...sourceDiagnostics,
			...assignedDiagnostics,
			serviceMatch: {
				...asObject(sourceDiagnostics.serviceMatch),
				...asObject(assignedDiagnostics.serviceMatch),
				...(asObject(sourceDiagnostics.serviceMatch).previous
					? { previous: sourceDiagnostics.serviceMatch.previous }
					: {}),
			},
			frequencyMatch: hasAssignedFrequencyMatch
				? {
						...assignedFrequencyMatch,
						...(Object.prototype.hasOwnProperty.call(
							sourceFrequencyMatch,
							'source',
						)
							? { source: sourceFrequencyMatch.source }
							: {}),
					}
				: sourceFrequencyMatch,
			sourceValues: {
				...asObject(assignedDiagnostics.sourceValues),
				...asObject(sourceDiagnostics.sourceValues),
			},
			assignment: {
				...asObject(sourceDiagnostics.assignment),
				...asObject(assignedDiagnostics.assignment),
				source: {
					...asObject(assignedDiagnostics.assignment?.source),
					...asObject(sourceDiagnostics.assignment?.source),
				},
			},
		};
		if (!Object.keys(diagnostics.serviceMatch).length)
			delete diagnostics.serviceMatch;
		if (!Object.keys(diagnostics.frequencyMatch).length)
			delete diagnostics.frequencyMatch;
		if (!Object.keys(diagnostics.assignment).length)
			delete diagnostics.assignment;
		if (markValidated || Object.keys(assignedDiagnostics).length)
			delete diagnostics.needsRevalidation;

		if (clientRowId) diagnostics.clientRowId = clientRowId;

		return {
			...order,
			...(clientRowId ? { [IMPORT_ROW_ID_FIELD]: clientRowId } : {}),
			importDiagnostics: diagnostics,
		};
	});
};

export const stripImportMetadata = orders =>
	asArray(orders).map(order => {
		const sanitizedOrder = { ...order };
		delete sanitizedOrder.importDiagnostics;
		delete sanitizedOrder[IMPORT_ROW_ID_FIELD];
		return sanitizedOrder;
	});

const CONTRACT_FREQUENCY_STATUSES = new Set([
	'resolved',
	'blank_preserved',
	'blank',
	'unknown',
	'ambiguous',
	'inactive',
	'review_required',
	'contract_conflict',
]);
const CONTRACT_FREQUENCY_STRATEGIES = new Set([
	'id',
	'code',
	'name',
	'alias',
	'existing',
	'manual',
]);
const CONTRACT_FREQUENCY_ACTIVITY_CODES = new Set([
	'CONTRACT_FREQUENCY_PRESERVED',
	'CONTRACT_FREQUENCY_MANUALLY_SELECTED',
]);

const getTransportServiceMatch = value => {
	const match = asObject(value);
	const source = asObject(match.source);
	// The API re-runs service resolution from the imported source. Keep only the
	// source and cohort preconditions read before that pass; signed capabilities
	// and authorizations travel through the explicit selection arrays instead.
	const hasOwn = key => Object.prototype.hasOwnProperty.call(source, key);
	const sourceValue = hasOwn('value') ? source.value : match.source;
	const activityType = hasOwn('activityType')
		? source.activityType
		: Object.prototype.hasOwnProperty.call(match, 'activityType')
			? match.activityType
			: match.activity_type;
	const transportString = value =>
		typeof value === 'string' && value.trim() ? value.trim() : undefined;
	const isScalar = candidate =>
		candidate === null ||
		['string', 'number', 'boolean'].includes(typeof candidate);
	const transportSource = {
		...(isScalar(sourceValue) ? { value: sourceValue } : {}),
		...(isScalar(activityType) ? { activityType } : {}),
	};
	const status = transportString(match.status);
	const strategy = transportString(match.strategy);
	const cohortKey = transportString(match.cohortKey);
	return Object.keys(transportSource).length || status || strategy || cohortKey
		? {
				...(Object.keys(transportSource).length
					? { source: transportSource }
					: {}),
				...(status ? { status } : {}),
				...(strategy ? { strategy } : {}),
				...(cohortKey ? { cohortKey } : {}),
			}
		: undefined;
};

const getTransportFrequencyDescriptor = value => {
	const descriptor = asObject(value);
	const frequencyId = Number(descriptor.frequencyId);
	if (!Number.isInteger(frequencyId) || frequencyId <= 0) return;
	const intervalCount = Number(descriptor.intervalCount);
	const code =
		typeof descriptor.code === 'string' ? descriptor.code.trim() : '';
	const name =
		typeof descriptor.name === 'string' ? descriptor.name.trim() : '';
	const intervalUnit =
		typeof descriptor.intervalUnit === 'string'
			? descriptor.intervalUnit.trim()
			: '';
	return {
		frequencyId,
		...(code ? { code } : {}),
		...(name ? { name } : {}),
		...(Number.isInteger(intervalCount) && intervalCount > 0
			? { intervalCount }
			: {}),
		...(intervalUnit ? { intervalUnit } : {}),
	};
};

const getTransportFrequencyMatch = value => {
	const match = asObject(value);
	const status = String(match.status || '').toLowerCase();
	const strategy = String(match.strategy || '').toLowerCase();
	const reviewChallenge =
		typeof match.reviewChallenge === 'string'
			? match.reviewChallenge.trim()
			: '';
	const source = asObject(match.source);
	const existing = getTransportFrequencyDescriptor(match.existing);
	const requested = getTransportFrequencyDescriptor(match.requested);
	const assigned = getTransportFrequencyDescriptor(match.assigned);
	const activity = asObject(match.activity);
	const hasScalarSource =
		Object.prototype.hasOwnProperty.call(source, 'value') &&
		(source.value === null ||
			['string', 'number', 'boolean'].includes(typeof source.value));
	let existingPayload = {};
	if (match.existing === null) existingPayload = { existing: null };
	else if (existing) existingPayload = { existing };
	let assignedPayload = {};
	if (match.assigned === null) assignedPayload = { assigned: null };
	else if (assigned) assignedPayload = { assigned };
	let requestedPayload = {};
	if (match.requested === null) requestedPayload = { requested: null };
	else if (requested) requestedPayload = { requested };
	const transportMatch = {
		...(CONTRACT_FREQUENCY_STATUSES.has(status) ? { status } : {}),
		...(CONTRACT_FREQUENCY_STRATEGIES.has(strategy)
			? { strategy }
			: match.strategy === null
				? { strategy: null }
				: {}),
		...(reviewChallenge ? { reviewChallenge } : {}),
		...(hasScalarSource ? { source: { value: source.value } } : {}),
		...existingPayload,
		...requestedPayload,
		...assignedPayload,
		...(typeof match.mutable === 'boolean' ? { mutable: match.mutable } : {}),
		...(CONTRACT_FREQUENCY_ACTIVITY_CODES.has(activity.code) &&
		activity.blocking === false
			? { activity: { code: activity.code, blocking: false } }
			: match.activity === null
				? { activity: null }
				: {}),
	};
	return Object.keys(transportMatch).length ? transportMatch : undefined;
};

const getTransportImportDiagnostics = order => {
	const diagnostics = getImportDiagnostics(order);
	const assignment = asObject(diagnostics.assignment);
	const serviceMatch = getTransportServiceMatch(
		diagnostics.serviceMatch || diagnostics.service_match,
	);
	const frequencyMatch = getTransportFrequencyMatch(diagnostics.frequencyMatch);
	const transportDiagnostics = {
		...(hasValue(diagnostics.clientRowId)
			? { clientRowId: String(diagnostics.clientRowId) }
			: {}),
		...(hasValue(diagnostics.rowNumber)
			? { rowNumber: diagnostics.rowNumber }
			: {}),
		...(Object.keys(asObject(diagnostics.sourceValues)).length
			? { sourceValues: diagnostics.sourceValues }
			: {}),
		...(Object.keys(asObject(diagnostics.sourceProvenance)).length
			? { sourceProvenance: diagnostics.sourceProvenance }
			: {}),
		...(Object.keys(asObject(diagnostics.sourceFieldValues)).length
			? { sourceFieldValues: diagnostics.sourceFieldValues }
			: {}),
		...(asArray(diagnostics.dateDefaults).length
			? { dateDefaults: diagnostics.dateDefaults }
			: {}),
		...(Object.keys(asObject(diagnostics.serviceReviewSession)).length
			? { serviceReviewSession: diagnostics.serviceReviewSession }
			: {}),
		...(Object.keys(asObject(diagnostics.fieldReview)).length
			? { fieldReview: diagnostics.fieldReview }
			: {}),
		...(Object.keys(asObject(assignment.source)).length
			? { assignment: { source: assignment.source } }
			: {}),
		...(serviceMatch ? { serviceMatch } : {}),
		...(frequencyMatch ? { frequencyMatch } : {}),
		...(Object.keys(asObject(diagnostics.serviceCreation?.request)).length
			? {
					serviceCreation: {
						request: diagnostics.serviceCreation.request,
					},
				}
			: {}),
	};
	return Object.keys(transportDiagnostics).length
		? transportDiagnostics
		: undefined;
};

export const prepareImportOrdersForAssignment = (
	orders,
	{ preserveDiagnostics = true } = {},
) =>
	asArray(orders).map(order => {
		const sanitizedOrder = { ...order };
		delete sanitizedOrder.importDiagnostics;
		const rowId = getImportRowIdentity(order);
		if (hasValue(rowId)) sanitizedOrder[IMPORT_ROW_ID_FIELD] = String(rowId);
		const transportDiagnostics = getTransportImportDiagnostics(order);
		if (preserveDiagnostics && transportDiagnostics)
			sanitizedOrder.importDiagnostics = transportDiagnostics;
		return sanitizedOrder;
	});

export const hasAssignmentApprovalRequired = orders =>
	asArray(orders).some(
		order => getImportDiagnostics(order).assignment?.approvalRequired === true,
	);

export const getImportCreationOutcome = (response, submittedOrders = []) => {
	const envelope = asObject(response);
	const resultOrders = Array.isArray(response)
		? response
		: asArray(envelope.orders);
	const getResultRowId = order => {
		const value = getImportRowIdentity(order);
		return hasValue(value) ? String(value) : undefined;
	};
	const explicitSucceeded = new Set(
		asArray(envelope.succeededRowIds).filter(hasValue).map(String),
	);
	const explicitFailed = new Set(
		asArray(envelope.failedRowIds).filter(hasValue).map(String),
	);
	resultOrders.forEach(order => {
		const rowId = getResultRowId(order);
		if (!rowId) return;
		const status = String(order?.status || '').toLowerCase();
		if (status === 'success') explicitSucceeded.add(rowId);
		if (
			['unsuccess', 'error', 'failed'].includes(status) ||
			order?.code === 'ORDER_ASSIGNMENT_CHANGED_DURING_IMPORT' ||
			asArray(order?.errors).some(
				error => error?.code === 'ORDER_ASSIGNMENT_CHANGED_DURING_IMPORT',
			) ||
			asArray(getImportDiagnostics(order).issues).some(
				issue => issue?.code === 'ORDER_ASSIGNMENT_CHANGED_DURING_IMPORT',
			)
		)
			explicitFailed.add(rowId);
	});
	const envelopeStatus = String(envelope.status || '').toLowerCase();
	const isStale =
		envelope.code === 'IMPORT_REVIEW_STALE' ||
		envelope.name === 'IMPORT_REVIEW_STALE';
	if (isStale) {
		const submittedRowIds = asArray(submittedOrders)
			.map(getResultRowId)
			.filter(Boolean);
		if (!explicitFailed.size)
			submittedRowIds
				.filter(rowId => !explicitSucceeded.has(rowId))
				.forEach(rowId => explicitFailed.add(rowId));
		return {
			isPartial: false,
			isStale: true,
			resultOrders,
			outcomes: asArray(envelope.outcomes),
			succeededRowIds: Array.from(explicitSucceeded),
			failedRowIds: Array.from(explicitFailed),
			message: envelope.message,
			reason: envelope.reason,
		};
	}
	const isPartial =
		['partial', 'stale', 'unsuccess', 'error', 'failed'].includes(
			envelopeStatus,
		) ||
		envelope.code === 'ORDER_ASSIGNMENT_CHANGED_DURING_IMPORT' ||
		explicitFailed.size > 0;
	if (!isPartial)
		return {
			isPartial: false,
			isStale: false,
			resultOrders,
			outcomes: asArray(envelope.outcomes),
			succeededRowIds: Array.from(explicitSucceeded),
			failedRowIds: Array.from(explicitFailed),
		};

	const submittedRowIds = asArray(submittedOrders)
		.map(getResultRowId)
		.filter(Boolean);
	// Fail closed: only explicitly successful rows are removed. Any unknown row
	// remains visible for the user to revalidate.
	const failedRowIds = new Set(explicitFailed);
	if (!failedRowIds.size)
		submittedRowIds
			.filter(rowId => !explicitSucceeded.has(rowId))
			.forEach(rowId => failedRowIds.add(rowId));
	return {
		isPartial: true,
		isStale: false,
		resultOrders,
		outcomes: asArray(envelope.outcomes),
		succeededRowIds: Array.from(explicitSucceeded),
		failedRowIds: Array.from(failedRowIds),
		message: envelope.message,
	};
};
